import * as React from "react"
import { Map as OlMap, View, Feature, Overlay } from "ol"
import TileLayer from "ol/layer/Tile"
import VectorLayer from "ol/layer/Vector"
import OSM from "ol/source/OSM"
import VectorSource from "ol/source/Vector"
import { fromLonLat } from "ol/proj"
import { createEmpty, extend as extendExtent, isEmpty as isExtentEmpty } from "ol/extent"
import { createXYZ } from "ol/tilegrid"
import { Fill, Style, Stroke, RegularShape } from "ol/style"
import CircleStyle from "ol/style/Circle"
import LineString from "ol/geom/LineString"
import Polygon from "ol/geom/Polygon"
import MultiPolygon from "ol/geom/MultiPolygon"
import Point from "ol/geom/Point"
import MultiPoint from "ol/geom/MultiPoint"
import Geometry from "ol/geom/Geometry"
import DragPan from "ol/interaction/DragPan"
import Select from "ol/interaction/Select"
import Modify from "ol/interaction/Modify"
import Translate from "ol/interaction/Translate"
import Snap from "ol/interaction/Snap"
import Draw from "ol/interaction/Draw"
import { click as clickCondition } from "ol/events/condition"
import MapBrowserEvent from "ol/MapBrowserEvent"
import { unByKey } from "ol/Observable"
import GeoJSON from "ol/format/GeoJSON"
import { FeatureLike } from "ol/Feature"
import { PaintClass, PaintStyle, ToolMode } from "./utils/types"
import { hexToRgba, createRenderOrderFunction } from "./utils/utils"
import { mergeOverlappingPolygons } from "./utils/merge_utils"
import { DatasetLayer, Layer } from "../projects/state"
import { DBModels } from "../projects/state/db_models"
import { reifyLayer } from "../projects/reify_layer"
import BaseLayer from "ol/layer/Base"
import ImageLayer from "ol/layer/Image"
import ImageStatic from "ol/source/ImageStatic"
import type { DatasetCache, ModelOutputCache } from "../projects/map/map_view"
import {
  olImageLayerFromLocalPreview,
  parseLocalRgbGeotiffPreview,
  type LocalGeotiffLayerState,
} from "../projects/map/local_geotiff_preview"
import { parsePaintbrushLocalGeotiffOrderId } from "./local_geotiff_constants"
import { getDataset } from "../projects/state/saved_dataset"
import { BooleanTileGrid, CategoricalTileGrid, tileGridStats } from "../projects/modelling/tile_grid"
import { installWebglRenderGuard } from "../projects/map/webgl_render_guard"

type PaintbrushMapProps = {
  activeTool: ToolMode
  paintStyle: PaintStyle
  selectedClass: PaintClass | null
  opacity: number
  classes: PaintClass[]
  snapToBoundaryEnabled: boolean
  updateVerticesOnOverlapEnabled: boolean
  osmLayerVisible: boolean
  annotationLayerVisible: boolean
  osmLayerOpacity: number
  annotationLayerOpacity: number
  projectLayers: Array<{ id: string; layer: Layer }>
  layerOrder: string[]
  /** Ephemeral local RGB GeoTIFF previews (not persisted with annotation). */
  localGeotiffLayers: Record<string, LocalGeotiffLayerState>
  onLocalGeotiffsParsed?: (layers: LocalGeotiffLayerState[]) => void
  dbModels: DBModels
  teamId: number
  onProjectLayerStatsChange?: (layerId: string, stats: tileGridStats) => void
  onDatasetLayerStyleMetadataChange?: (layerId: string, updates: Partial<DatasetLayer>) => void
  onUndoRedoStateChange?: (canUndo: boolean, canRedo: boolean) => void
  onDataChange?: () => void
  geojsonUrl?: string
  onClassesRestored?: (classes: PaintClass[]) => void
  onLoadingChange?: (isLoading: boolean) => void
}

export type PaintbrushMapRef = {
  undo: () => void
  redo: () => void
  canUndo: () => boolean
  canRedo: () => boolean
  exportGeoJSON: () => string | null
  importTileGridAsVectors: (
    tileGrid: BooleanTileGrid | CategoricalTileGrid,
    classByValue: Record<number, { classId: number; color: string }>
  ) => void
  importGeoJSONFeatures: (
    geojson: unknown,
    classByValue: Record<number, { classId: number; color: string }>
  ) => void
}

const TRANSIENT_KEY = "__transient"
const POINT_KEY = "__isPoint"
const POINT_COLOR = "#f59e0b"

export const PaintbrushMap = React.forwardRef<PaintbrushMapRef, PaintbrushMapProps>(
  ({
    activeTool,
    paintStyle,
    selectedClass,
    opacity,
    classes,
    snapToBoundaryEnabled,
    updateVerticesOnOverlapEnabled,
    osmLayerVisible,
    annotationLayerVisible,
    osmLayerOpacity,
    annotationLayerOpacity,
    projectLayers,
    layerOrder,
    localGeotiffLayers,
    onLocalGeotiffsParsed,
    dbModels,
    teamId,
    onProjectLayerStatsChange,
    onDatasetLayerStyleMetadataChange,
    onUndoRedoStateChange,
    onDataChange,
    geojsonUrl,
    onClassesRestored,
    onLoadingChange
  }, ref) => {
  const mapRef = React.useRef<HTMLDivElement>(null)
  const [map, setMap] = React.useState<OlMap | null>(null)
  const localOlByIdRef = React.useRef(new globalThis.Map<string, ImageLayer<ImageStatic>>())
  const [localGeotiffDropMessage, setLocalGeotiffDropMessage] = React.useState<string | null>(null)
  const [localGeotiffLoading, setLocalGeotiffLoading] = React.useState(false)
  const vectorSourceRef = React.useRef<VectorSource | null>(null)
  const vectorLayerRef = React.useRef<VectorLayer<VectorSource> | null>(null)
  const osmLayerRef = React.useRef<TileLayer<OSM> | null>(null)
  const projectLayerByIdRef = React.useRef<Record<string, BaseLayer>>({})
  const modelOutputCacheRef = React.useRef<ModelOutputCache>({})
  const datasetCacheRef = React.useRef<DatasetCache>({})
  const loadingDatasetIdsRef = React.useRef<Set<number>>(new Set())
  const [datasetCacheVersion, setDatasetCacheVersion] = React.useState(0)
  const layerStatsSignatureRef = React.useRef<Record<string, string>>({})
  const onProjectLayerStatsChangeRef = React.useRef(onProjectLayerStatsChange)
  const dragPanInteractionsRef = React.useRef<DragPan[]>([])
  const classesRef = React.useRef<PaintClass[]>(classes)
  const styleCacheRef = React.useRef<Record<string, Style>>({})

  // Freehand painting state
  const isPaintingRef = React.useRef(false)
  const currentStrokeFeatureRef = React.useRef<Feature | null>(null)
  const currentStrokeCoordsRef = React.useRef<number[][]>([])
  const animationFrameRef = React.useRef<number | null>(null)

  // Polygon (click-to-vertex) drawing state
  const isDrawingPolygonRef = React.useRef(false)
  const polygonCoordsRef = React.useRef<number[][]>([])
  const polygonPointerCoordRef = React.useRef<number[] | null>(null)
  const polygonPreviewFeatureRef = React.useRef<Feature | null>(null)
  const snapIndicatorFeatureRef = React.useRef<Feature | null>(null)

  // Cursor-mode interactions
  const selectInteractionRef = React.useRef<Select | null>(null)
  const modifyInteractionRef = React.useRef<Modify | null>(null)
  const translateInteractionRef = React.useRef<Translate | null>(null)

  // Selection state for UI panels
  const [selectedEditFeature, setSelectedEditFeature] = React.useState<Feature | null>(null)
  const [selectedPointFeature, setSelectedPointFeature] = React.useState<Feature | null>(null)
  const [pointNoteDraft, setPointNoteDraft] = React.useState<string>("")
  const [splitMode, setSplitMode] = React.useState(false)

  // Point note popup overlay
  const pointPopupElementRef = React.useRef<HTMLDivElement | null>(null)
  const pointPopupOverlayRef = React.useRef<Overlay | null>(null)

  // Undo/redo history
  const historyRef = React.useRef<any[]>([])
  const historyIndexRef = React.useRef<number>(-1)
  const maxHistorySize = 50
  const isRestoringStateRef = React.useRef(false)
  const geoJsonFormat = React.useRef<GeoJSON>(new GeoJSON())

  const saveState = React.useCallback((markDirty: boolean = true) => {
    const source = vectorSourceRef.current
    if (!source || isRestoringStateRef.current) return

    const features = source.getFeatures().filter(feature => !feature.get(TRANSIENT_KEY))
    const state = features.map(feature => {
      const geometry = feature.getGeometry()
      if (!geometry) return null
      const geoJson = geoJsonFormat.current.writeFeatureObject(feature)
      return {
        geoJson,
        classId: feature.get("classId"),
        strokeColor: feature.get("strokeColor"),
        opacity: feature.get("opacity"),
        isPoint: Boolean(feature.get(POINT_KEY)),
        note: feature.get("note")
      }
    }).filter(f => f !== null)

    const currentIndex = historyIndexRef.current
    if (currentIndex < historyRef.current.length - 1) {
      historyRef.current = historyRef.current.slice(0, currentIndex + 1)
    }

    historyRef.current.push(state)

    if (historyRef.current.length > maxHistorySize) {
      historyRef.current.shift()
    } else {
      historyIndexRef.current = historyRef.current.length - 1
    }

    if (onUndoRedoStateChange) {
      onUndoRedoStateChange(
        historyIndexRef.current >= 0 || historyRef.current.length > 0,
        historyIndexRef.current < historyRef.current.length - 1
      )
    }

    if (markDirty && onDataChange) {
      onDataChange()
    }
  }, [onUndoRedoStateChange, onDataChange])

  const restoreState = React.useCallback((state: any[]) => {
    const source = vectorSourceRef.current
    if (!source) return

    isRestoringStateRef.current = true
    source.clear()

    state.forEach(item => {
      try {
        const feature = geoJsonFormat.current.readFeature(item.geoJson, {
          dataProjection: "EPSG:3857",
          featureProjection: "EPSG:3857"
        }) as Feature<Geometry>
        feature.set("classId", item.classId)
        feature.set("strokeColor", item.strokeColor)
        feature.set("opacity", item.opacity)
        if (item.isPoint) feature.set(POINT_KEY, true)
        if (item.note !== undefined) feature.set("note", item.note)
        source.addFeature(feature)
      } catch (e) {
        console.warn("Failed to restore feature:", e)
      }
    })

    isRestoringStateRef.current = false
  }, [])

  const undo = React.useCallback(() => {
    if (historyIndexRef.current < 0) return

    historyIndexRef.current--
    const state = historyIndexRef.current >= 0 ? historyRef.current[historyIndexRef.current] : []
    restoreState(state)

    if (onUndoRedoStateChange) {
      onUndoRedoStateChange(
        historyIndexRef.current >= 0 || historyRef.current.length > 0,
        historyIndexRef.current < historyRef.current.length - 1
      )
    }
  }, [restoreState, onUndoRedoStateChange])

  const redo = React.useCallback(() => {
    if (historyIndexRef.current >= historyRef.current.length - 1) return

    historyIndexRef.current++
    const state = historyRef.current[historyIndexRef.current]
    restoreState(state)

    if (onUndoRedoStateChange) {
      onUndoRedoStateChange(
        historyIndexRef.current >= 0 || historyRef.current.length > 0,
        historyIndexRef.current < historyRef.current.length - 1
      )
    }
  }, [restoreState, onUndoRedoStateChange])

  const exportGeoJSON = React.useCallback((): string | null => {
    if (!vectorSourceRef.current || !geoJsonFormat.current) return null

    const features = vectorSourceRef.current.getFeatures().filter(feature => !feature.get(TRANSIENT_KEY))
    if (features.length === 0) return null

    const geoJsonFeatures = features.map(feature => {
      const geometry = feature.getGeometry()
      if (!geometry) return null

      const geoJson = geoJsonFormat.current.writeFeatureObject(feature)

      if (feature.get(POINT_KEY)) {
        geoJson.properties = {
          type: "point",
          note: feature.get("note") ?? ""
        }
        return geoJson
      }

      const classId = feature.get("classId")
      const paintClass = classesRef.current.find(c => c.id === classId)
      const classIndex = classesRef.current.findIndex(c => c.id === classId)

      geoJson.properties = {
        classId: classId,
        className: paintClass?.name || null,
        color: paintClass?.color || feature.get("strokeColor") || "#ff7f50",
        opacity: feature.get("opacity") ?? opacity,
        order: classIndex >= 0 ? classIndex : null
      }

      return geoJson
    }).filter(f => f !== null)

    const classesMetadata = classesRef.current.map((paintClass, index) => ({
      id: paintClass.id,
      name: paintClass.name,
      color: paintClass.color,
      opacity: paintClass.opacity ?? 1,
      order: index
    }))

    const featureCollection = {
      type: "FeatureCollection",
      metadata: {
        classes: classesMetadata,
        exportedAt: new Date().toISOString()
      },
      features: geoJsonFeatures
    }

    return JSON.stringify(featureCollection, null, 2)
  }, [opacity])

  const importTileGridAsVectors = React.useCallback((
    tileGrid: BooleanTileGrid | CategoricalTileGrid,
    classByValue: Record<number, { classId: number; color: string }>
  ) => {
    const source = vectorSourceRef.current
    const mapInstance = map
    if (!source || !mapInstance) return

    source.clear()
    const tileGridDef = createXYZ()
    const features: Array<Feature<Geometry>> = []
    const combinedExtent = createEmpty()
    type Run = { startX: number; endX: number; value: number }
    type Rect = { startX: number; endX: number; startY: number; endY: number; value: number }
    const activeRects: globalThis.Map<string, Rect> = new globalThis.Map<string, Rect>()
    const finalizedRects: Rect[] = []

    // Raster compression pass: merge adjacent cells into vertical rectangles.
    // This preserves shape boundaries while massively reducing feature count.
    for (let y = tileGrid.y; y < tileGrid.y + tileGrid.height; y += 1) {
      const runs: Run[] = []
      let x = tileGrid.x

      while (x < tileGrid.x + tileGrid.width) {
        const raw = tileGrid.get(x, y, tileGrid.zoom)
        const value = Number(raw)
        const isActiveValue =
          tileGrid instanceof BooleanTileGrid ? Boolean(raw) : value !== 255

        if (!isActiveValue || !classByValue[value]) {
          x += 1
          continue
        }

        let endX = x
        while (endX + 1 < tileGrid.x + tileGrid.width) {
          const nextRaw = tileGrid.get(endX + 1, y, tileGrid.zoom)
          const nextValue = Number(nextRaw)
          const nextIsActive =
            tileGrid instanceof BooleanTileGrid ? Boolean(nextRaw) : nextValue !== 255
          if (!nextIsActive || nextValue !== value || !classByValue[nextValue]) break
          endX += 1
        }

        runs.push({ startX: x, endX, value })
        x = endX + 1
      }

      const nextActiveRects: globalThis.Map<string, Rect> = new globalThis.Map<string, Rect>()
      const usedKeys = new Set<string>()

      runs.forEach((run) => {
        const key = `${run.value}:${run.startX}:${run.endX}`
        const existing = activeRects.get(key)
        if (existing && existing.endY === y - 1) {
          existing.endY = y
          nextActiveRects.set(key, existing)
        } else {
          nextActiveRects.set(key, {
            startX: run.startX,
            endX: run.endX,
            startY: y,
            endY: y,
            value: run.value
          })
        }
        usedKeys.add(key)
      })

      activeRects.forEach((rect, key) => {
        if (!usedKeys.has(key)) finalizedRects.push(rect)
      })

      activeRects.clear()
      nextActiveRects.forEach((rect, key) => activeRects.set(key, rect))
    }

    activeRects.forEach((rect) => finalizedRects.push(rect))

    finalizedRects.forEach((rect) => {
      const classInfo = classByValue[rect.value]
      if (!classInfo) return

      const startExtent = tileGridDef.getTileCoordExtent([tileGrid.zoom, rect.startX, rect.startY])
      const endExtent = tileGridDef.getTileCoordExtent([tileGrid.zoom, rect.endX, rect.endY])
      const minX = Math.min(startExtent[0], endExtent[0])
      const minY = Math.min(startExtent[1], endExtent[1])
      const maxX = Math.max(startExtent[2], endExtent[2])
      const maxY = Math.max(startExtent[3], endExtent[3])

      const polygon = new Polygon([[
        [minX, minY],
        [maxX, minY],
        [maxX, maxY],
        [minX, maxY],
        [minX, minY]
      ]])
      const feature = new Feature({ geometry: polygon })
      feature.set("classId", classInfo.classId)
      feature.set("strokeColor", classInfo.color)
      feature.set("opacity", opacity)
      features.push(feature)
      extendExtent(combinedExtent, polygon.getExtent())
    })

    if (features.length === 0) {
      alert("No vectorizable features were found in this dataset.")
      return
    }

    let outputFeatures = features

    // Optional dissolve pass: connect touching rectangles of same class.
    // This greatly improves continuity without creating one polygon per tile.
    const jsts = (window as any).jsts
    if (jsts?.io?.GeoJSONReader && jsts?.io?.GeoJSONWriter) {
      try {
        const reader = new jsts.io.GeoJSONReader()
        const writer = new jsts.io.GeoJSONWriter()
        const sampleExtent = tileGridDef.getTileCoordExtent([tileGrid.zoom, tileGrid.x, tileGrid.y])
        const cellWidth = Math.abs(sampleExtent[2] - sampleExtent[0])
        const cellHeight = Math.abs(sampleExtent[3] - sampleExtent[1])
        const closeGapEpsilon = Math.max(Math.min(cellWidth, cellHeight) * 0.0025, 1e-9)
        const groupedByClass: Record<string, Feature<Geometry>[]> = {}
        features.forEach((feature) => {
          const key = String(feature.get("classId") ?? "")
          if (!groupedByClass[key]) groupedByClass[key] = []
          groupedByClass[key].push(feature)
        })

        const dissolved: Array<Feature<Geometry>> = []
        Object.values(groupedByClass).forEach((classFeatures) => {
          if (classFeatures.length <= 1) {
            dissolved.push(...classFeatures)
            return
          }

          const geometries = classFeatures
            .map((feature) => {
              const geometry = feature.getGeometry()
              if (!(geometry instanceof Polygon || geometry instanceof MultiPolygon)) return null
              const geo = geoJsonFormat.current.writeGeometryObject(geometry)
              let jstsGeometry = reader.read(geo)
              try { jstsGeometry = jstsGeometry.buffer(0) } catch {}
              try { jstsGeometry = jstsGeometry.buffer(closeGapEpsilon) } catch {}
              return jstsGeometry
            })
            .filter(Boolean)

          if (geometries.length === 0) return

          let merged = geometries[0]
          try {
            const UnaryUnionOp = jsts.operation?.union?.UnaryUnionOp
            if (UnaryUnionOp) {
              const geomFactory = geometries[0].getFactory()
              const geometryCollection = geomFactory.createGeometryCollection(geometries)
              merged = UnaryUnionOp.union(geometryCollection)
            } else {
              for (let i = 1; i < geometries.length; i += 1) {
                try {
                  merged = merged.union(geometries[i])
                } catch {
                  // Keep progressing even if a specific union fails.
                }
              }
            }
          } catch {
            for (let i = 1; i < geometries.length; i += 1) {
              try {
                merged = merged.union(geometries[i])
              } catch {
                // Keep progressing even if a specific union fails.
              }
            }
          }
          try { merged = merged.buffer(0) } catch {}
          try { merged = merged.buffer(-closeGapEpsilon) } catch {}
          try { merged = merged.buffer(0) } catch {}

          const mergedGeo = writer.write(merged)
          const mergedOlGeometry = geoJsonFormat.current.readGeometry(mergedGeo, {
            dataProjection: "EPSG:3857",
            featureProjection: "EPSG:3857"
          })
          const first = classFeatures[0]
          const mergedFeature = new Feature({ geometry: mergedOlGeometry })
          mergedFeature.set("classId", first.get("classId"))
          mergedFeature.set("strokeColor", first.get("strokeColor"))
          mergedFeature.set("opacity", first.get("opacity"))
          dissolved.push(mergedFeature)
        })

        if (dissolved.length > 0) {
          outputFeatures = dissolved
        }
      } catch (error) {
        console.warn("Dataset dissolve pass failed; keeping rectangle merge output.", error)
      }
    }

    source.addFeatures(outputFeatures)
    styleCacheRef.current = {}
    vectorLayerRef.current?.changed()
    saveState()

    if (!isExtentEmpty(combinedExtent)) {
      mapInstance.getView().fit(combinedExtent, {
        padding: [40, 40, 40, 40],
        maxZoom: 19,
        duration: 500
      })
    }
  }, [map, opacity, saveState])

  const importGeoJSONFeatures = React.useCallback((
    geojson: unknown,
    classByValue: Record<number, { classId: number; color: string }>
  ) => {
    const source = vectorSourceRef.current
    const mapInstance = map
    if (!source || !mapInstance) return

    const rawFeatures = geoJsonFormat.current.readFeatures(geojson as object, {
      dataProjection: "EPSG:4326",
      featureProjection: "EPSG:3857"
    }) as Feature<Geometry>[]

    const normalizedFeatures: Feature<Geometry>[] = []
    const combinedExtent = createEmpty()
    rawFeatures.forEach((feature) => {
      const geometry = feature.getGeometry()
      if (!(geometry instanceof Polygon || geometry instanceof MultiPolygon)) return
      const props = feature.getProperties() as Record<string, unknown>
      const rawValue =
        props.DN ?? props.dn ?? props.VALUE ?? props.value ?? props.class ?? props.classId ?? props.gridcode
      const value = Number(rawValue)
      const classInfo = classByValue[value]
      if (!classInfo) return
      feature.set("classId", classInfo.classId)
      feature.set("strokeColor", classInfo.color)
      feature.set("opacity", opacity)
      normalizedFeatures.push(feature)
      extendExtent(combinedExtent, geometry.getExtent())
    })

    if (normalizedFeatures.length === 0) {
      alert("No importable polygon features found in server vector output.")
      return
    }

    const chunkedFeatures: Feature<Geometry>[] = []
    const jsts = (window as any).jsts
    const TARGET_CHUNK_AREA = 1_500_000 // smaller max box size for editing
    const MAX_GRID_SPLITS_PER_AXIS = 18
    const MIN_CHUNK_VERTEX_COUNT = 120
    const SIMPLIFY_TOLERANCE = 6
    const SIMPLIFY_TOLERANCE_LARGE = 12

    const getVertexCount = (geometry: Geometry): number => {
      if (geometry instanceof Polygon) {
        return geometry.getCoordinates().reduce((count, ring) => count + ring.length, 0)
      }
      if (geometry instanceof MultiPolygon) {
        return geometry.getCoordinates().reduce(
          (count, polygon) => count + polygon.reduce((ringCount, ring) => ringCount + ring.length, 0),
          0
        )
      }
      return 0
    }

    const simplifyGeometryForEditing = (geometry: Geometry): Geometry => {
      const vertexCount = getVertexCount(geometry)
      const tolerance = vertexCount > 1200 ? SIMPLIFY_TOLERANCE_LARGE : SIMPLIFY_TOLERANCE
      try {
        return geometry.simplify(tolerance)
      } catch {
        return geometry
      }
    }

    const pushFeatureFromGeometry = (geometry: Geometry, template: Feature<Geometry>) => {
      if (geometry instanceof Polygon) {
        const simplified = simplifyGeometryForEditing(geometry)
        const feature = new Feature({ geometry: simplified })
        feature.set("classId", template.get("classId"))
        feature.set("strokeColor", template.get("strokeColor"))
        feature.set("opacity", template.get("opacity"))
        chunkedFeatures.push(feature)
        return
      }
      if (geometry instanceof MultiPolygon) {
        geometry.getPolygons().forEach((polygon) => {
          const simplified = simplifyGeometryForEditing(polygon)
          const feature = new Feature({ geometry: simplified })
          feature.set("classId", template.get("classId"))
          feature.set("strokeColor", template.get("strokeColor"))
          feature.set("opacity", template.get("opacity"))
          chunkedFeatures.push(feature)
        })
      }
    }

    const maybeChunkFeature = (feature: Feature<Geometry>) => {
      const geometry = feature.getGeometry()
      if (!(geometry instanceof Polygon || geometry instanceof MultiPolygon)) return

      const area = Math.abs(geometry.getArea())
      const vertexCount = getVertexCount(geometry)
      if (area <= TARGET_CHUNK_AREA && vertexCount <= MIN_CHUNK_VERTEX_COUNT) {
        pushFeatureFromGeometry(geometry, feature)
        return
      }

      if (!jsts?.io?.GeoJSONReader || !jsts?.io?.GeoJSONWriter) {
        pushFeatureFromGeometry(geometry, feature)
        return
      }

      const extent = geometry.getExtent()
      const width = Math.max(extent[2] - extent[0], 1)
      const height = Math.max(extent[3] - extent[1], 1)
      const splitFactor = Math.max(2, Math.ceil(Math.sqrt(area / TARGET_CHUNK_AREA)))
      const cols = Math.min(MAX_GRID_SPLITS_PER_AXIS, splitFactor)
      const rows = Math.min(MAX_GRID_SPLITS_PER_AXIS, splitFactor)
      const cellWidth = width / cols
      const cellHeight = height / rows
      const overlapX = Math.max(cellWidth * 0.002, 0.5)
      const overlapY = Math.max(cellHeight * 0.002, 0.5)

      try {
        const reader = new jsts.io.GeoJSONReader()
        const writer = new jsts.io.GeoJSONWriter()
        const geometryObj = geoJsonFormat.current.writeGeometryObject(geometry)
        let sourceJstsGeom = reader.read(geometryObj)
        try { sourceJstsGeom = sourceJstsGeom.buffer(0) } catch {}

        for (let row = 0; row < rows; row += 1) {
          for (let col = 0; col < cols; col += 1) {
            const minX = extent[0] + col * cellWidth
            const minY = extent[1] + row * cellHeight
            const maxX = col === cols - 1 ? extent[2] : minX + cellWidth
            const maxY = row === rows - 1 ? extent[3] : minY + cellHeight
            const clipMinX = col === 0 ? minX : minX - overlapX
            const clipMinY = row === 0 ? minY : minY - overlapY
            const clipMaxX = col === cols - 1 ? maxX : maxX + overlapX
            const clipMaxY = row === rows - 1 ? maxY : maxY + overlapY

            const clipPolygon = new Polygon([[
              [clipMinX, clipMinY],
              [clipMaxX, clipMinY],
              [clipMaxX, clipMaxY],
              [clipMinX, clipMaxY],
              [clipMinX, clipMinY]
            ]])
            const clipObj = geoJsonFormat.current.writeGeometryObject(clipPolygon)
            let clipJstsGeom = reader.read(clipObj)
            try { clipJstsGeom = clipJstsGeom.buffer(0) } catch {}

            let intersection = null
            try {
              intersection = sourceJstsGeom.intersection(clipJstsGeom)
            } catch {
              intersection = null
            }
            if (!intersection) continue
            const jstsIntersection = intersection as { isEmpty?: () => boolean }
            if (typeof jstsIntersection.isEmpty === "function" && jstsIntersection.isEmpty()) continue

            const intersectGeoObj = writer.write(intersection)
            const intersectOlGeom = geoJsonFormat.current.readGeometry(intersectGeoObj, {
              dataProjection: "EPSG:3857",
              featureProjection: "EPSG:3857"
            })
            if (!intersectOlGeom) continue
            pushFeatureFromGeometry(intersectOlGeom, feature)
          }
        }
      } catch {
        pushFeatureFromGeometry(geometry, feature)
      }
    }

    normalizedFeatures.forEach(maybeChunkFeature)

    const finalFeatures = chunkedFeatures.length > 0 ? chunkedFeatures : normalizedFeatures
    source.clear()
    source.addFeatures(finalFeatures)
    styleCacheRef.current = {}
    vectorLayerRef.current?.changed()
    saveState()

    if (!isExtentEmpty(combinedExtent)) {
      mapInstance.getView().fit(combinedExtent, {
        padding: [40, 40, 40, 40],
        maxZoom: 19,
        duration: 500
      })
    }
  }, [map, opacity, saveState])

  React.useImperativeHandle(ref, () => ({
    undo,
    redo,
    canUndo: () => historyIndexRef.current >= 0 || historyRef.current.length > 0,
    canRedo: () => historyIndexRef.current < historyRef.current.length - 1,
    exportGeoJSON,
    importTileGridAsVectors,
    importGeoJSONFeatures
  }), [undo, redo, exportGeoJSON, importTileGridAsVectors, importGeoJSONFeatures])

  // Global Ctrl/Cmd+Z / Ctrl/Cmd+Shift+Z shortcuts
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement
      if (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable) {
        return
      }

      if ((event.ctrlKey || event.metaKey) && event.key === "z" && !event.shiftKey) {
        event.preventDefault()
        undo()
      } else if ((event.ctrlKey || event.metaKey) && ((event.shiftKey && event.key === "z") || event.key === "y")) {
        event.preventDefault()
        redo()
      }
    }

    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [undo, redo])

  const handleMergePolygons = React.useCallback((newPolygon: Polygon, classId: number): Feature[] => {
    if (!vectorSourceRef.current) return []

    const classOpacity = selectedClass?.opacity ?? 1
    const effectiveOpacity = classOpacity * opacity
    return mergeOverlappingPolygons(newPolygon, classId, {
      vectorSource: vectorSourceRef.current,
      geoJsonFormat: geoJsonFormat.current,
      selectedClass,
      opacity: effectiveOpacity
    })
  }, [selectedClass, opacity])

  React.useEffect(() => {
    classesRef.current = classes
  }, [classes])

  React.useEffect(() => {
    onProjectLayerStatsChangeRef.current = onProjectLayerStatsChange
  }, [onProjectLayerStatsChange])

  React.useEffect(() => {
    if (!mapRef.current) return

    installWebglRenderGuard()

    const indonesiaCenter = fromLonLat([120, -2])
    const vectorSource = new VectorSource()
    vectorSourceRef.current = vectorSource

    const vectorLayer = new VectorLayer({
      source: vectorSource,
      renderOrder: createRenderOrderFunction(classesRef.current),
      style: (feature) => {
        const f = feature as Feature
        // Point annotations: render as a star.
        if (f.get(POINT_KEY)) {
          const pointCacheKey = "__point-star"
          if (!styleCacheRef.current[pointCacheKey]) {
            styleCacheRef.current[pointCacheKey] = new Style({
              image: new RegularShape({
                points: 5,
                radius: 10,
                radius2: 4,
                angle: 0,
                fill: new Fill({ color: POINT_COLOR }),
                stroke: new Stroke({ color: "#ffffff", width: 2 })
              })
            })
          }
          return styleCacheRef.current[pointCacheKey]
        }

        const classId = f.get("classId")
        const paintClass = classesRef.current.find(c => c.id === classId)
        const color = paintClass?.color || f.get("strokeColor") || "#ff7f50"
        const strokeWidthPixels = 2
        const geometry = f.getGeometry()
        const featureOpacity = f.get("opacity") ?? opacity
        const opacityKey = Math.round(featureOpacity * 100) / 100
        const isPolygon = geometry instanceof Polygon || geometry instanceof MultiPolygon
        const cacheKey = `${color}-${opacityKey}-${isPolygon ? "poly" : "line"}`

        if (!styleCacheRef.current[cacheKey]) {
          const styleConfig: { stroke: Stroke; fill?: Fill } = {
            stroke: new Stroke({
              color: hexToRgba(color, featureOpacity),
              width: strokeWidthPixels,
              lineCap: "round",
              lineJoin: "round"
            })
          }

          if (isPolygon) {
            styleConfig.fill = new Fill({
              color: hexToRgba(color, featureOpacity)
            })
          }

          styleCacheRef.current[cacheKey] = new Style(styleConfig)
        }

        return styleCacheRef.current[cacheKey]
      }
    })
    vectorLayerRef.current = vectorLayer

    const osmLayer = new TileLayer({ source: new OSM() })
    osmLayerRef.current = osmLayer

    const newMap = new OlMap({
      target: mapRef.current,
      layers: [
        osmLayer,
        vectorLayer
      ],
      view: new View({
        center: indonesiaCenter,
        zoom: 5
      })
    })

    setMap(newMap)

    historyRef.current = [[]]
    historyIndexRef.current = 0

    const viewport = newMap.getViewport()
    const onContextLost = (event: Event) => {
      event.preventDefault()
    }
    viewport.addEventListener("webglcontextlost", onContextLost, true)

    return () => {
      viewport.removeEventListener("webglcontextlost", onContextLost, true)
      newMap.setTarget(undefined)
      newMap.dispose()
      vectorSourceRef.current = null
      vectorLayerRef.current = null
      osmLayerRef.current = null
    }
  }, [])

  React.useEffect(() => {
    if (osmLayerRef.current) {
      osmLayerRef.current.setVisible(osmLayerVisible)
      map?.render()
    }
  }, [osmLayerVisible, map])

  React.useEffect(() => {
    if (osmLayerRef.current) {
      osmLayerRef.current.setOpacity(osmLayerOpacity)
      map?.render()
    }
  }, [osmLayerOpacity, map])

  React.useEffect(() => {
    if (vectorLayerRef.current) {
      vectorLayerRef.current.setVisible(annotationLayerVisible)
      map?.render()
    }
  }, [annotationLayerVisible, map])

  React.useEffect(() => {
    if (vectorLayerRef.current) {
      vectorLayerRef.current.setOpacity(annotationLayerOpacity)
      map?.render()
    }
  }, [annotationLayerOpacity, map])

  React.useEffect(() => {
    if (!map) return

    const mapLayers = map.getLayers()

    const loadDataset = (layer: DatasetLayer) => {
      if (!teamId || loadingDatasetIdsRef.current.has(layer.id)) return
      loadingDatasetIdsRef.current.add(layer.id)
      const layerEntry = projectLayers.find(entry => entry.layer.type === "DatasetLayer" && entry.layer.id === layer.id)
      getDataset(layer.id, teamId, (err, out, colorMetadata) => {
        loadingDatasetIdsRef.current.delete(layer.id)
        if (!err && out) {
          datasetCacheRef.current[layer.id] = out
          if (layerEntry && colorMetadata && (colorMetadata.colors || colorMetadata.fill)) {
            const updates: Partial<DatasetLayer> = {}
            if (colorMetadata.colors) updates.colors = colorMetadata.colors
            if (colorMetadata.fill) updates.fill = colorMetadata.fill
            onDatasetLayerStyleMetadataChange?.(layerEntry.id, updates)
          }
          setDatasetCacheVersion(version => version + 1)
        }
      })
    }

    const projectLayerLookup: Record<string, Layer> = {}
    projectLayers.forEach(entry => {
      projectLayerLookup[entry.id] = entry.layer
    })

    const wantedLocalUuids = new Set<string>()
    for (const id of layerOrder) {
      const u = parsePaintbrushLocalGeotiffOrderId(id)
      if (u !== null && localGeotiffLayers[u]) {
        wantedLocalUuids.add(u)
      }
    }
    for (const k of Array.from(localOlByIdRef.current.keys())) {
      if (!wantedLocalUuids.has(k)) {
        localOlByIdRef.current.delete(k)
      }
    }

    const normalizedOrder = layerOrder.filter(id => {
      if (id === "osm" || id === "annotations") return true
      if (projectLayerLookup[id]) return true
      const localUuid = parsePaintbrushLocalGeotiffOrderId(id)
      return localUuid !== null && Boolean(localGeotiffLayers[localUuid])
    })
    if (!normalizedOrder.includes("annotations")) normalizedOrder.unshift("annotations")

    const nextProjectLayerById: Record<string, BaseLayer> = {}
    const desiredLayers: BaseLayer[] = []

    normalizedOrder.slice().reverse().forEach(id => {
      if (id === "osm") {
        if (osmLayerRef.current) {
          desiredLayers.push(osmLayerRef.current)
        }
        return
      }
      if (id === "annotations") {
        if (vectorLayerRef.current) {
          desiredLayers.push(vectorLayerRef.current)
        }
        return
      }

      const localUuid = parsePaintbrushLocalGeotiffOrderId(id)
      if (localUuid !== null) {
        const data = localGeotiffLayers[localUuid]
        if (!data) return
        let im = localOlByIdRef.current.get(localUuid) ?? null
        if (!im) {
          const fresh = olImageLayerFromLocalPreview(data) as ImageLayer<ImageStatic>
          localOlByIdRef.current.set(localUuid, fresh)
          im = fresh
        }
        im.setOpacity(data.opacity)
        im.setVisible(data.visible)
        desiredLayers.push(im)
        return
      }

      const layerSpec = projectLayerLookup[id]
      if (!layerSpec) return
      try {
        if (layerSpec.type === "DatasetLayer" && datasetCacheRef.current[layerSpec.id]) {
          const stats = datasetCacheRef.current[layerSpec.id].getStats()
          const signature = JSON.stringify(stats)
          if (layerStatsSignatureRef.current[id] !== signature) {
            layerStatsSignatureRef.current[id] = signature
            onProjectLayerStatsChangeRef.current?.(id, stats)
          }
        }
        if (layerSpec.type === "ModelOutputLayer" && modelOutputCacheRef.current[layerSpec.nodeId]) {
          const stats = modelOutputCacheRef.current[layerSpec.nodeId].getStats()
          const signature = JSON.stringify(stats)
          if (layerStatsSignatureRef.current[id] !== signature) {
            layerStatsSignatureRef.current[id] = signature
            onProjectLayerStatsChangeRef.current?.(id, stats)
          }
        }
        const layer = reifyLayer(
          layerSpec,
          projectLayerByIdRef.current[id] ?? null,
          dbModels,
          map,
          modelOutputCacheRef.current,
          datasetCacheRef.current,
          loadDataset
        )
        nextProjectLayerById[id] = layer
        layer.setVisible(layerSpec.visible)
        layer.setOpacity(layerSpec.opacity)
        desiredLayers.push(layer)
      } catch (error) {
        console.warn(`Unable to reify paintbrush layer: ${layerSpec.name}`, error)
      }
    })

    projectLayerByIdRef.current = nextProjectLayerById

    // Reconcile in place to avoid clear/re-add flicker.
    const currentLayers = mapLayers.getArray().slice()
    const desiredSet = new Set(desiredLayers)

    currentLayers.forEach(layer => {
      if (!desiredSet.has(layer)) {
        mapLayers.remove(layer)
      }
    })

    desiredLayers.forEach((layer, targetIndex) => {
      const atIndex = mapLayers.item(targetIndex)
      if (atIndex === layer) return
      const existingIndex = mapLayers.getArray().indexOf(layer)
      if (existingIndex >= 0) {
        mapLayers.removeAt(existingIndex)
      }
      mapLayers.insertAt(targetIndex, layer)
    })

    map.render()
  }, [map, projectLayers, layerOrder, localGeotiffLayers, dbModels, teamId, datasetCacheVersion])

  React.useEffect(() => {
    if (map === null) return
    const el = map.getViewport()
    const opts: AddEventListenerOptions = { capture: true }

    const looksLikeTif = (f: File) =>
      /\.(tif|tiff)$/i.test(f.name) ||
      f.type === "image/tiff" ||
      f.type === "image/geotiff" ||
      (f.type === "application/octet-stream" && /\.(tif|tiff)$/i.test(f.name))

    const filesFromDataTransfer = (dt: DataTransfer): File[] => {
      const fromFiles = dt.files?.length ? Array.from(dt.files) : []
      if (fromFiles.length > 0) {
        return fromFiles
      }
      const out: File[] = []
      if (dt.items) {
        for (let i = 0; i < dt.items.length; i++) {
          const item = dt.items[i]
          if (item.kind === "file") {
            const f = item.getAsFile()
            if (f) {
              out.push(f)
            }
          }
        }
      }
      return out
    }

    const onDragEnter = (e: DragEvent) => {
      e.preventDefault()
    }
    const onDragOver = (e: DragEvent) => {
      e.preventDefault()
      if (e.dataTransfer) {
        e.dataTransfer.dropEffect = "copy"
      }
    }
    const onDrop = (e: DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      if (!e.dataTransfer) {
        return
      }
      const raw = filesFromDataTransfer(e.dataTransfer)
      if (raw.length === 0) {
        return
      }
      const tifFiles = raw.filter(looksLikeTif)
      if (tifFiles.length === 0) {
        setLocalGeotiffDropMessage("Drop a .tif / .tiff GeoTIFF file to preview it on the map (not saved).")
        window.setTimeout(() => setLocalGeotiffDropMessage(null), 5000)
        return
      }
      setLocalGeotiffDropMessage(null)
      const mapInstance = map
      void (async () => {
        setLocalGeotiffLoading(true)
        try {
          const additions: LocalGeotiffLayerState[] = []
          for (const file of tifFiles) {
            const buf = await file.arrayBuffer()
            const preview = await parseLocalRgbGeotiffPreview(buf)
            const id =
              typeof crypto !== "undefined" && crypto.randomUUID
                ? crypto.randomUUID()
                : `local-geotiff-${Date.now()}-${Math.random().toString(36).slice(2)}`
            additions.push({
              ...preview,
              id,
              name: file.name,
              visible: true,
              opacity: 0.92,
            })
          }
          onLocalGeotiffsParsed?.(additions)
          if (additions.length > 0) {
            let merged = createEmpty()
            for (const a of additions) {
              extendExtent(merged, a.extent3857)
            }
            if (!isExtentEmpty(merged)) {
              window.requestAnimationFrame(() => {
                mapInstance.getView().fit(merged, { duration: 550, padding: [48, 48, 48, 48], maxZoom: 18 })
              })
            }
          }
        } catch (err) {
          const message = err instanceof Error ? err.message : String(err)
          setLocalGeotiffDropMessage(message)
          window.setTimeout(() => setLocalGeotiffDropMessage(null), 8000)
        } finally {
          setLocalGeotiffLoading(false)
        }
      })()
    }

    el.addEventListener("dragenter", onDragEnter, opts)
    el.addEventListener("dragover", onDragOver, opts)
    el.addEventListener("drop", onDrop, opts)
    return () => {
      el.removeEventListener("dragenter", onDragEnter, opts)
      el.removeEventListener("dragover", onDragOver, opts)
      el.removeEventListener("drop", onDrop, opts)
    }
  }, [map, onLocalGeotiffsParsed])

  // GeoJSON loading (unchanged behaviour — just supports point annotations now)
  const geojsonLoadedRef = React.useRef<string | null>(null)
  const isLoadingGeoJSONRef = React.useRef(false)
  const onClassesRestoredRef = React.useRef(onClassesRestored)
  const saveStateRef = React.useRef(saveState)
  const onLoadingChangeRef = React.useRef(onLoadingChange)

  React.useEffect(() => {
    onClassesRestoredRef.current = onClassesRestored
    saveStateRef.current = saveState
    onLoadingChangeRef.current = onLoadingChange
  }, [onClassesRestored, saveState, onLoadingChange])

  React.useEffect(() => {
    if (!geojsonUrl || !vectorSourceRef.current || !geoJsonFormat.current || !map) return

    if (geojsonLoadedRef.current === geojsonUrl) return
    if (isLoadingGeoJSONRef.current) return

    const loadGeoJSON = async () => {
      try {
        isLoadingGeoJSONRef.current = true
        geojsonLoadedRef.current = geojsonUrl
        if (onLoadingChangeRef.current) onLoadingChangeRef.current(true)

        const response = await fetch(geojsonUrl)
        if (!response.ok) {
          console.warn("Failed to load GeoJSON:", response.statusText)
          geojsonLoadedRef.current = null
          isLoadingGeoJSONRef.current = false
          if (onLoadingChangeRef.current) onLoadingChangeRef.current(false)
          return
        }

        const geoJsonData = await response.json()

        if (geoJsonData.metadata?.classes && onClassesRestoredRef.current && classes.length === 0) {
          const restoredClasses: PaintClass[] = geoJsonData.metadata.classes
            .slice()
            .sort((a: any, b: any) => a.order - b.order)
            .map((c: any) => ({
              id: Number(c.id),
              name: String(c.name || "Unnamed Class"),
              color: String(c.color || "#ff7f50"),
              opacity: typeof c.opacity === "number" ? c.opacity : 1
            }))

          if (restoredClasses.length > 0) {
            onClassesRestoredRef.current(restoredClasses)
          }
        }

        const features = geoJsonFormat.current.readFeatures(geoJsonData, {
          dataProjection: "EPSG:3857",
          featureProjection: "EPSG:3857"
        }) as Feature<Geometry>[]

        features.forEach(feature => {
          const props = feature.getProperties()
          if (props.type === "point" || feature.getGeometry() instanceof Point) {
            feature.set(POINT_KEY, true)
            if (typeof props.note === "string") feature.set("note", props.note)
          } else {
            if (props.classId !== undefined) feature.set("classId", props.classId)
            if (props.color) feature.set("strokeColor", props.color)
            if (props.opacity !== undefined) feature.set("opacity", props.opacity)
          }
        })

        if (vectorSourceRef.current) {
          if (vectorSourceRef.current.getFeatures().length > 0) {
            vectorSourceRef.current.clear()
          }
          vectorSourceRef.current.addFeatures(features)

          const polygonExtent = createEmpty()
          features.forEach(feature => {
            const geom = feature.getGeometry()
            if (!geom) return
            if (geom instanceof Polygon || geom instanceof MultiPolygon) {
              extendExtent(polygonExtent, geom.getExtent())
            }
          })
          if (!isExtentEmpty(polygonExtent) && map) {
            map.getView().fit(polygonExtent, {
              padding: [40, 40, 40, 40],
              maxZoom: 19,
              duration: 1200
            })
          }

          setTimeout(() => {
            // Initial GeoJSON hydration establishes baseline state and should not mark unsaved changes.
            if (saveStateRef.current) saveStateRef.current(false)
          }, 100)
        }
      } catch (error) {
        console.error("Error loading GeoJSON:", error)
        geojsonLoadedRef.current = null
      } finally {
        isLoadingGeoJSONRef.current = false
        if (onLoadingChangeRef.current) onLoadingChangeRef.current(false)
      }
    }

    loadGeoJSON()
  }, [geojsonUrl, map, classes.length])

  // Classes change: remove features from deleted classes, update styles + render order
  const previousClassesRef = React.useRef<PaintClass[]>(classes)

  React.useEffect(() => {
    if (!map || !vectorSourceRef.current) return

    const previousClasses = previousClassesRef.current
    const currentClassIds = new Set(classes.map(c => c.id))

    const deletedClassIds = previousClasses
      .filter(prevClass => !currentClassIds.has(prevClass.id))
      .map(prevClass => prevClass.id)

    if (deletedClassIds.length > 0) {
      const features = vectorSourceRef.current.getFeatures()
      const featuresToRemove = features.filter(feature => {
        if (feature.get(POINT_KEY)) return false
        const featureClassId = feature.get("classId")
        return featureClassId !== null && featureClassId !== undefined && deletedClassIds.includes(featureClassId)
      })

      featuresToRemove.forEach(feature => {
        vectorSourceRef.current?.removeFeature(feature)
      })

      if (featuresToRemove.length > 0) {
        saveState()
      }
    }

    classesRef.current = classes
    previousClassesRef.current = classes

    styleCacheRef.current = {}

    if (vectorLayerRef.current && vectorSourceRef.current) {
      vectorLayerRef.current.setRenderOrder(createRenderOrderFunction(classes))
      vectorLayerRef.current.changed()
    }
  }, [map, classes])

  // Base-opacity change: update all non-point features to reflect classOpacity * base
  React.useEffect(() => {
    if (!map || !vectorSourceRef.current) return

    const features = vectorSourceRef.current.getFeatures()
    features.forEach(feature => {
      if (feature.get(POINT_KEY)) return
      const classId = feature.get("classId")
      const paintClass = classId != null ? classes.find(c => c.id === classId) : null
      const classOpacity = paintClass?.opacity ?? 1
      const effectiveOpacity = classOpacity * opacity
      feature.set("opacity", effectiveOpacity)
      feature.changed()
    })

    styleCacheRef.current = {}

    if (vectorLayerRef.current) vectorLayerRef.current.changed()
  }, [map, opacity, classes])

  // Collect OL's built-in DragPan interaction(s) so we can toggle panning
  React.useEffect(() => {
    if (!map) return
    const dragPans = map
      .getInteractions()
      .getArray()
      .filter((interaction): interaction is DragPan => interaction instanceof DragPan)
    dragPanInteractionsRef.current = dragPans
  }, [map])

  React.useEffect(() => {
    // Pan disabled while actively drawing (Paint) or erasing; allowed in Cursor/Point
    const allowPan = !(activeTool === ToolMode.Paint && selectedClass) && activeTool !== ToolMode.Erase
    dragPanInteractionsRef.current.forEach(interaction => interaction.setActive(allowPan))
  }, [activeTool, selectedClass])

  React.useEffect(() => {
    if (!map) return
    const viewport = map.getViewport()
    if (!viewport) return
    if (activeTool === ToolMode.Paint || activeTool === ToolMode.Point) {
      viewport.style.cursor = "crosshair"
    } else if (activeTool === ToolMode.Erase) {
      viewport.style.cursor = "pointer"
    } else {
      viewport.style.cursor = ""
    }
  }, [map, activeTool])

  // Paint (freehand OR polygon-click)
  React.useEffect(() => {
    if (!map || !vectorSourceRef.current) return
    if (activeTool !== ToolMode.Paint || !selectedClass) {
      isPaintingRef.current = false
      if (currentStrokeFeatureRef.current && vectorSourceRef.current) {
        vectorSourceRef.current.removeFeature(currentStrokeFeatureRef.current)
        currentStrokeFeatureRef.current = null
        currentStrokeCoordsRef.current = []
      }
      isDrawingPolygonRef.current = false
      polygonCoordsRef.current = []
      if (polygonPreviewFeatureRef.current && vectorSourceRef.current) {
        vectorSourceRef.current.removeFeature(polygonPreviewFeatureRef.current)
        polygonPreviewFeatureRef.current = null
      }
      return
    }

    const source = vectorSourceRef.current

    const setSnapIndicator = (coordinate: number[] | null) => {
      if (!source) return
      if (!coordinate) {
        if (snapIndicatorFeatureRef.current) {
          source.removeFeature(snapIndicatorFeatureRef.current)
          snapIndicatorFeatureRef.current = null
        }
        return
      }

      if (!snapIndicatorFeatureRef.current) {
        const feature = new Feature({ geometry: new Point(coordinate) })
        feature.set(TRANSIENT_KEY, true)
        feature.setStyle(new Style({
          image: new CircleStyle({
            radius: 4,
            fill: new Fill({ color: "#22c55e" }),
            stroke: new Stroke({ color: "#ffffff", width: 2 })
          })
        }))
        snapIndicatorFeatureRef.current = feature
        source.addFeature(feature)
      } else {
        const geom = snapIndicatorFeatureRef.current.getGeometry()
        if (geom instanceof Point) {
          geom.setCoordinates(coordinate)
        } else {
          snapIndicatorFeatureRef.current.setGeometry(new Point(coordinate))
        }
      }
    }

    const cancelPolygon = () => {
      isDrawingPolygonRef.current = false
      polygonCoordsRef.current = []
      polygonPointerCoordRef.current = null
      if (polygonPreviewFeatureRef.current) {
        source.removeFeature(polygonPreviewFeatureRef.current)
        polygonPreviewFeatureRef.current = null
      }
      setSnapIndicator(null)
    }

    const trimPolygonAgainstOtherClasses = (polygon: Polygon): Polygon => {
      if (!snapToBoundaryEnabled || !selectedClass) return polygon
      try {
        const jsts = (window as any).jsts
        if (!jsts?.io?.GeoJSONReader || !jsts?.io?.GeoJSONWriter) return polygon

        const jstsReader = new jsts.io.GeoJSONReader()
        const jstsWriter = new jsts.io.GeoJSONWriter()

        const precisionScale = 1e6
        const reducer =
          jsts?.precision?.GeometryPrecisionReducer && jsts?.geom?.PrecisionModel
            ? new jsts.precision.GeometryPrecisionReducer(new jsts.geom.PrecisionModel(precisionScale))
            : null
        if (reducer) {
          reducer.setChangePrecisionModel(true)
          reducer.setRemoveCollapsedComponents(true)
        }

        const newPolyGeo = geoJsonFormat.current.writeGeometryObject(polygon)
        let newJstsGeom = jstsReader.read(newPolyGeo)
        try { newJstsGeom = newJstsGeom.buffer(0) } catch {}
        if (reducer) { try { newJstsGeom = reducer.reduce(newJstsGeom) } catch {} }

        const others = source.getFeatures().filter(f => {
          if (f.get(TRANSIENT_KEY)) return false
          if (f.get(POINT_KEY)) return false
          const classId = f.get("classId")
          if (classId == null || classId === selectedClass.id) return false
          const g = f.getGeometry()
          return g instanceof Polygon || g instanceof MultiPolygon
        })

        for (const f of others) {
          const g = f.getGeometry() as Polygon | MultiPolygon
          const otherGeo = geoJsonFormat.current.writeGeometryObject(g)
          let otherJsts = jstsReader.read(otherGeo)
          try { otherJsts = otherJsts.buffer(0) } catch {}
          if (reducer) { try { otherJsts = reducer.reduce(otherJsts) } catch {} }

          if (newJstsGeom.intersects(otherJsts) || newJstsGeom.touches(otherJsts)) {
            try {
              const diff = newJstsGeom.difference(otherJsts)
              newJstsGeom = diff
              try { newJstsGeom = newJstsGeom.buffer(0) } catch {}
              if (reducer) { try { newJstsGeom = reducer.reduce(newJstsGeom) } catch {} }
            } catch {
              // fall through and keep the last-known good geometry
            }
          }
        }

        const trimmedGeo = jstsWriter.write(newJstsGeom)
        const trimmedOl = geoJsonFormat.current.readGeometry(trimmedGeo, {
          dataProjection: "EPSG:3857",
          featureProjection: "EPSG:3857"
        })
        if (trimmedOl instanceof Polygon) return trimmedOl
        return polygon
      } catch {
        return polygon
      }
    }

    const carvePolygonOutOfOtherClasses = (carvePolygon: Polygon, currentClassId: number) => {
      if (!vectorSourceRef.current) return
      const sourceFeatures = vectorSourceRef.current.getFeatures()
      const jsts = (window as any).jsts
      if (!jsts?.io?.GeoJSONReader || !jsts?.io?.GeoJSONWriter) return

      try {
        const reader = new jsts.io.GeoJSONReader()
        const writer = new jsts.io.GeoJSONWriter()
        const carveGeo = geoJsonFormat.current.writeGeometryObject(carvePolygon)
        let carveJsts = reader.read(carveGeo)
        try { carveJsts = carveJsts.buffer(0) } catch {}

        sourceFeatures.forEach(feature => {
          if (feature.get(TRANSIENT_KEY) || feature.get(POINT_KEY)) return
          const featureClassId = feature.get("classId")
          if (featureClassId == null || featureClassId === currentClassId) return
          const geometry = feature.getGeometry()
          if (!(geometry instanceof Polygon || geometry instanceof MultiPolygon)) return

          try {
            const featureGeo = geoJsonFormat.current.writeGeometryObject(geometry)
            let featureJsts = reader.read(featureGeo)
            try { featureJsts = featureJsts.buffer(0) } catch {}
            if (!(featureJsts.intersects(carveJsts) || featureJsts.touches(carveJsts))) return

            let diff = featureJsts.difference(carveJsts)
            try { diff = diff.buffer(0) } catch {}
            if (!diff || (diff.isEmpty && diff.isEmpty())) {
              vectorSourceRef.current?.removeFeature(feature)
              return
            }

            const diffGeo = writer.write(diff)
            const diffOl = geoJsonFormat.current.readGeometry(diffGeo, {
              dataProjection: "EPSG:3857",
              featureProjection: "EPSG:3857"
            })
            if (!diffOl) {
              vectorSourceRef.current?.removeFeature(feature)
              return
            }
            feature.setGeometry(diffOl)
          } catch {
            // Keep feature unchanged if topology operation fails.
          }
        })
      } catch {
        // Fail safely: keep original geometries unchanged.
      }
    }

    const commitPolygon = () => {
      if (!selectedClass) return
      const coords = polygonCoordsRef.current
      if (coords.length < 3) return

      const closedCoords = [...coords]
      const first = coords[0]
      const last = coords[coords.length - 1]
      const isAlreadyClosed = first[0] === last[0] && first[1] === last[1]
      if (!isAlreadyClosed) closedCoords.push([first[0], first[1]])

      let polygon = new Polygon([closedCoords])
      if (updateVerticesOnOverlapEnabled) {
        carvePolygonOutOfOtherClasses(polygon, selectedClass.id)
      } else {
        polygon = trimPolygonAgainstOtherClasses(polygon)
      }

      if (polygonPreviewFeatureRef.current) {
        source.removeFeature(polygonPreviewFeatureRef.current)
        polygonPreviewFeatureRef.current = null
      }

      const mergedFeatures = handleMergePolygons(polygon, selectedClass.id)
      if (mergedFeatures.length > 0) {
        mergedFeatures.forEach(f => source.addFeature(f))
      } else {
        const feature = new Feature({ geometry: polygon })
        const classOpacity = selectedClass.opacity ?? 1
        const effectiveOpacity = classOpacity * opacity
        feature.set("strokeColor", selectedClass.color)
        feature.set("opacity", effectiveOpacity)
        feature.set("classId", selectedClass.id)
        source.addFeature(feature)
      }

      cancelPolygon()
      saveState()
    }

    if (paintStyle === PaintStyle.Polygon) {
      const updatePreview = () => {
        if (!selectedClass) return
        const coords = polygonCoordsRef.current
        if (coords.length === 0) return

        const pointerCoord = polygonPointerCoordRef.current
        const lineCoords =
          pointerCoord && isDrawingPolygonRef.current
            ? [...coords, pointerCoord]
            : coords
        const geometry = new LineString(lineCoords)

        if (!polygonPreviewFeatureRef.current) {
          const feature = new Feature({ geometry })
          const classOpacity = selectedClass.opacity ?? 1
          const effectiveOpacity = classOpacity * opacity
          feature.set("strokeColor", selectedClass.color)
          feature.set("opacity", effectiveOpacity)
          feature.set("classId", selectedClass.id)
          polygonPreviewFeatureRef.current = feature
          source.addFeature(feature)
        } else {
          polygonPreviewFeatureRef.current.setGeometry(geometry)
        }
      }

      const isCloseToFirstVertex = (coordinate: number[]) => {
        const coords = polygonCoordsRef.current
        if (coords.length < 3) return false
        const first = coords[0]
        const firstPx = map.getPixelFromCoordinate(first)
        const curPx = map.getPixelFromCoordinate(coordinate)
        const dx = firstPx[0] - curPx[0]
        const dy = firstPx[1] - curPx[1]
        return Math.hypot(dx, dy) <= 10
      }

      const snapToBoundary = (coordinate: number[]): number[] => {
        if (!snapToBoundaryEnabled) {
          setSnapIndicator(null)
          return coordinate
        }
        if (!selectedClass || !vectorSourceRef.current) return coordinate

        const resolution = map.getView().getResolution() || 1

        const existingFeatures = vectorSourceRef.current.getFeatures().filter(feature => {
          if (feature.get(TRANSIENT_KEY) || feature.get(POINT_KEY)) return false
          const featureClassId = feature.get("classId")
          const geometry = feature.getGeometry()
          return featureClassId !== null &&
                 featureClassId !== undefined &&
                 featureClassId !== selectedClass.id &&
                 geometry instanceof Polygon
        })

        const isInsideOtherClass = existingFeatures.some(feature => {
          const geometry = feature.getGeometry()
          return geometry instanceof Polygon && geometry.intersectsCoordinate(coordinate)
        })
        const snapTolerancePixels = isInsideOtherClass ? 35 : 10
        const snapTolerance = resolution * snapTolerancePixels

        if (existingFeatures.length === 0) return coordinate

        let closestPoint: number[] | null = null
        let closestDistance = Infinity
        let closestBoundaryCoords: number[][] | null = null

        existingFeatures.forEach(feature => {
          const geometry = feature.getGeometry() as Polygon
          const exteriorRing = geometry.getLinearRing(0)
          if (!exteriorRing) return

          const ringCoords = exteriorRing.getCoordinates() as number[][]
          const boundaryLine = new LineString(ringCoords)

          const closestOnBoundary = boundaryLine.getClosestPoint(coordinate)
          const distance = Math.hypot(
            closestOnBoundary[0] - coordinate[0],
            closestOnBoundary[1] - coordinate[1]
          )

          if (distance < snapTolerance && distance < closestDistance) {
            closestDistance = distance
            closestPoint = closestOnBoundary
            closestBoundaryCoords = ringCoords
          }
        })

        if (closestPoint && closestBoundaryCoords) {
          const boundaryCoords: number[][] = closestBoundaryCoords

          let bestSegmentDistance = Infinity
          let bestPointOnSegment: number[] | null = null

          for (let i = 0; i < boundaryCoords.length - 1; i++) {
            const segStart = boundaryCoords[i]
            const segEnd = boundaryCoords[i + 1]
            const segLine = new LineString([segStart, segEnd])
            const closestOnSeg = segLine.getClosestPoint(coordinate)
            const segDist = Math.hypot(
              closestOnSeg[0] - coordinate[0],
              closestOnSeg[1] - coordinate[1]
            )

            if (segDist < bestSegmentDistance) {
              bestSegmentDistance = segDist
              bestPointOnSegment = closestOnSeg
            }
          }

          if (bestSegmentDistance < snapTolerance && bestPointOnSegment) {
            setSnapIndicator(bestPointOnSegment)
            return bestPointOnSegment
          }

          setSnapIndicator(closestPoint)
          return closestPoint
        }

        setSnapIndicator(null)
        return coordinate
      }

      const handleClick = (event: MapBrowserEvent<UIEvent>) => {
        const snappedCoord = snapToBoundary(event.coordinate)

        if (isDrawingPolygonRef.current && isCloseToFirstVertex(snappedCoord)) {
          commitPolygon()
        } else {
          isDrawingPolygonRef.current = true
          polygonCoordsRef.current = [...polygonCoordsRef.current, snappedCoord]
          polygonPointerCoordRef.current = snappedCoord
          updatePreview()
        }
        setSnapIndicator(null)
        event.preventDefault()
        event.stopPropagation()
      }

      const handlePointerMove = (event: MapBrowserEvent<UIEvent>) => {
        if (!isDrawingPolygonRef.current) return
        const snappedCoord = snapToBoundary(event.coordinate)
        polygonPointerCoordRef.current = snappedCoord
        updatePreview()
      }

      const handleDblClick = (event: MapBrowserEvent<UIEvent>) => {
        if (!isDrawingPolygonRef.current) return
        event.preventDefault()
        event.stopPropagation()
        commitPolygon()
      }

      const handleKeyDown = (event: KeyboardEvent) => {
        const target = event.target as HTMLElement
        if (target?.tagName === "INPUT" || target?.tagName === "TEXTAREA" || target?.isContentEditable) return

        if (event.key === "Escape") {
          event.preventDefault()
          cancelPolygon()
          return
        }

        if (event.key === "Backspace") {
          if (!isDrawingPolygonRef.current) return
          event.preventDefault()
          polygonCoordsRef.current = polygonCoordsRef.current.slice(0, -1)
          if (polygonCoordsRef.current.length === 0) {
            cancelPolygon()
          } else {
            polygonPointerCoordRef.current = polygonCoordsRef.current[polygonCoordsRef.current.length - 1] ?? null
            if (polygonPreviewFeatureRef.current) {
              polygonPreviewFeatureRef.current.setGeometry(new LineString(polygonCoordsRef.current))
            }
          }
          return
        }

        if (event.key === "Enter") {
          if (!isDrawingPolygonRef.current) return
          event.preventDefault()
          commitPolygon()
        }
      }

      const clickKey = map.on("click" as any, handleClick)
      const pointerMoveKey = map.on("pointermove" as any, handlePointerMove)
      const dblClickKey = map.on("dblclick" as any, handleDblClick)
      window.addEventListener("keydown", handleKeyDown)

      return () => {
        unByKey(clickKey)
        unByKey(pointerMoveKey)
        unByKey(dblClickKey)
        window.removeEventListener("keydown", handleKeyDown)
        cancelPolygon()
      }
    }

    // --- Freehand (drag brush) ---
    const getResolution = () => map.getView().getResolution() || 1
    const mapDistance = (c1: number[], c2: number[]) => Math.hypot(c1[0] - c2[0], c1[1] - c2[1])

    const updateStroke = (coordinate: number[]) => {
      if (!vectorSourceRef.current || !selectedClass) return

      const resolution = getResolution()
      const minDistancePixels = 3
      const minDistanceMapUnits = minDistancePixels * resolution

      const coords = currentStrokeCoordsRef.current
      if (coords.length > 0) {
        const lastCoord = coords[coords.length - 1]
        if (mapDistance(lastCoord, coordinate) < minDistanceMapUnits) return
      }

      currentStrokeCoordsRef.current.push(coordinate)

      if (!currentStrokeFeatureRef.current) {
        const lineString = new LineString([coordinate])
        const feature = new Feature({ geometry: lineString })
        const classOpacity = selectedClass.opacity ?? 1
        const effectiveOpacity = classOpacity * opacity
        feature.set("strokeColor", selectedClass.color)
        feature.set("opacity", effectiveOpacity)
        feature.set("classId", selectedClass.id)
        currentStrokeFeatureRef.current = feature
        vectorSourceRef.current.addFeature(feature)
      } else {
        const geometry = currentStrokeFeatureRef.current.getGeometry() as LineString
        geometry.setCoordinates(currentStrokeCoordsRef.current)
      }
    }

    const scheduleUpdate = () => {
      if (animationFrameRef.current === null) {
        animationFrameRef.current = requestAnimationFrame(() => {
          animationFrameRef.current = null
        })
      }
    }

    const handlePointerDown = (event: MapBrowserEvent<UIEvent>) => {
      isPaintingRef.current = true
      currentStrokeCoordsRef.current = []
      currentStrokeFeatureRef.current = null
      updateStroke(event.coordinate)
      event.preventDefault()
      event.stopPropagation()
    }

    const handlePointerMove = (event: MapBrowserEvent<UIEvent>) => {
      if (!isPaintingRef.current || (event.originalEvent instanceof MouseEvent && event.originalEvent.buttons !== 1)) {
        return
      }
      updateStroke(event.coordinate)
      scheduleUpdate()
      event.preventDefault()
      event.stopPropagation()
    }

    const handlePointerUp = () => {
      isPaintingRef.current = false

      if (currentStrokeFeatureRef.current && currentStrokeCoordsRef.current.length >= 3) {
        const coords = currentStrokeCoordsRef.current
        const closedCoords = [...coords]
        const first = coords[0]
        const last = coords[coords.length - 1]

        if (first[0] !== last[0] || first[1] !== last[1]) {
          closedCoords.push([first[0], first[1]])
        }

        const feature = currentStrokeFeatureRef.current
        const classId = feature.get("classId")
        let polygon = new Polygon([closedCoords])
        if (updateVerticesOnOverlapEnabled) {
          carvePolygonOutOfOtherClasses(polygon, classId)
        } else {
          polygon = trimPolygonAgainstOtherClasses(polygon)
        }

        vectorSourceRef.current?.removeFeature(feature)

        const mergedFeatures = handleMergePolygons(polygon, classId)

        if (mergedFeatures.length > 0) {
          mergedFeatures.forEach(mergedFeature => {
            vectorSourceRef.current?.addFeature(mergedFeature)
          })
        } else {
          feature.setGeometry(polygon)
          vectorSourceRef.current?.addFeature(feature)
        }

        saveState()
      } else if (currentStrokeFeatureRef.current && currentStrokeCoordsRef.current.length < 3) {
        vectorSourceRef.current?.removeFeature(currentStrokeFeatureRef.current)
      }

      currentStrokeFeatureRef.current = null
      currentStrokeCoordsRef.current = []
    }

    const pointerDownKey = map.on("pointerdown" as any, handlePointerDown)
    const pointerDragKey = map.on("pointerdrag" as any, handlePointerMove)
    const pointerUpKey = map.on("pointerup" as any, handlePointerUp)

    return () => {
      isPaintingRef.current = false
      currentStrokeFeatureRef.current = null
      currentStrokeCoordsRef.current = []
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current)
        animationFrameRef.current = null
      }
      unByKey(pointerDownKey)
      unByKey(pointerDragKey)
      unByKey(pointerUpKey)
    }
  }, [map, activeTool, paintStyle, selectedClass, opacity, snapToBoundaryEnabled, updateVerticesOnOverlapEnabled, saveState, handleMergePolygons])

  // Erase — click to remove a feature
  React.useEffect(() => {
    if (!map || !vectorSourceRef.current) return
    if (activeTool !== ToolMode.Erase) return

    const handleClick = (event: MapBrowserEvent<UIEvent>) => {
      if (!vectorSourceRef.current) return

      const features = map.getFeaturesAtPixel(event.pixel, { hitTolerance: 5 })

      if (features && features.length > 0) {
        const actualFeatures = (features.filter(f => f instanceof Feature) as Feature[])
          .filter(f => !f.get(TRANSIENT_KEY))

        if (actualFeatures.length > 0) {
          vectorSourceRef.current.removeFeature(actualFeatures[0])
          saveState()
        }
      }

      event.preventDefault()
      event.stopPropagation()
    }

    const clickKey = map.on("click" as any, handleClick)

    return () => {
      unByKey(clickKey)
    }
  }, [map, activeTool, saveState])

  // Point tool — click to drop a star; reuses existing point on hit
  React.useEffect(() => {
    if (!map || !vectorSourceRef.current) return
    if (activeTool !== ToolMode.Point) return

    const source = vectorSourceRef.current

    const handleClick = (event: MapBrowserEvent<UIEvent>) => {
      const hit = map.forEachFeatureAtPixel(
        event.pixel,
        (feature, layer) => {
          if (layer !== vectorLayerRef.current) return null
          const f = feature as Feature
          if (f.get(TRANSIENT_KEY)) return null
          if (f.get(POINT_KEY)) return f
          return null
        },
        { hitTolerance: 8 }
      ) as Feature | null

      if (hit) {
        setSelectedPointFeature(hit)
      } else {
        const feature = new Feature({ geometry: new Point(event.coordinate) })
        feature.set(POINT_KEY, true)
        feature.set("note", "")
        source.addFeature(feature)
        setSelectedPointFeature(feature)
        saveState()
      }
      event.preventDefault()
      event.stopPropagation()
    }

    const clickKey = map.on("click" as any, handleClick)

    return () => {
      unByKey(clickKey)
    }
  }, [map, activeTool, saveState])

  // Clear any selected point when leaving cursor/point modes
  React.useEffect(() => {
    if (activeTool !== ToolMode.Cursor && activeTool !== ToolMode.Point) {
      setSelectedPointFeature(null)
    }
  }, [activeTool])

  // Keep the note-editor textarea synced with the selected point
  React.useEffect(() => {
    if (selectedPointFeature) {
      const note = selectedPointFeature.get("note")
      setPointNoteDraft(typeof note === "string" ? note : "")
    } else {
      setPointNoteDraft("")
    }
  }, [selectedPointFeature])

  // Attach the point popup as an OL Overlay
  React.useEffect(() => {
    if (!map || !pointPopupElementRef.current) return
    const overlay = new Overlay({
      element: pointPopupElementRef.current,
      positioning: "bottom-center",
      stopEvent: true,
      offset: [0, -20]
    })
    map.addOverlay(overlay)
    pointPopupOverlayRef.current = overlay

    return () => {
      map.removeOverlay(overlay)
      pointPopupOverlayRef.current = null
    }
  }, [map])

  // Position the point popup over the selected point (and follow if it moves)
  React.useEffect(() => {
    const overlay = pointPopupOverlayRef.current
    if (!overlay) return
    const shouldShow = !!selectedPointFeature &&
      (activeTool === ToolMode.Cursor || activeTool === ToolMode.Point)
    if (!shouldShow) {
      overlay.setPosition(undefined)
      return
    }
    const geometry = selectedPointFeature!.getGeometry()
    const updatePosition = () => {
      if (geometry instanceof Point) overlay.setPosition(geometry.getCoordinates())
    }
    updatePosition()
    const key = geometry?.on("change", updatePosition)
    return () => {
      if (key) unByKey(key as any)
    }
  }, [selectedPointFeature, activeTool])

  // Cursor mode — Select / Modify / Translate (+ optional Snap)
  React.useEffect(() => {
    if (!map || !vectorSourceRef.current || !vectorLayerRef.current) return
    if (activeTool !== ToolMode.Cursor) return

    const vectorLayer = vectorLayerRef.current
    const vectorSource = vectorSourceRef.current

    const trimFeatureAgainstOtherClasses = (feature: Feature): boolean => {
      const classId = feature.get("classId")
      if (classId == null) return false
      const geometry = feature.getGeometry()
      if (!(geometry instanceof Polygon) && !(geometry instanceof MultiPolygon)) return false

      try {
        const jsts = (window as any).jsts
        if (!jsts?.io?.GeoJSONReader || !jsts?.io?.GeoJSONWriter) return false

        const jstsReader = new jsts.io.GeoJSONReader()
        const jstsWriter = new jsts.io.GeoJSONWriter()

        const precisionScale = 1e6
        const reducer =
          jsts?.precision?.GeometryPrecisionReducer && jsts?.geom?.PrecisionModel
            ? new jsts.precision.GeometryPrecisionReducer(new jsts.geom.PrecisionModel(precisionScale))
            : null
        if (reducer) {
          reducer.setChangePrecisionModel(true)
          reducer.setRemoveCollapsedComponents(true)
        }

        const featureGeo = geoJsonFormat.current.writeGeometryObject(geometry)
        let featureJsts = jstsReader.read(featureGeo)
        try { featureJsts = featureJsts.buffer(0) } catch {}
        if (reducer) { try { featureJsts = reducer.reduce(featureJsts) } catch {} }

        const others = vectorSource.getFeatures().filter(f => {
          if (f === feature) return false
          if (f.get(TRANSIENT_KEY) || f.get(POINT_KEY)) return false
          const otherClassId = f.get("classId")
          if (otherClassId == null || otherClassId === classId) return false
          const g = f.getGeometry()
          return g instanceof Polygon || g instanceof MultiPolygon
        })

        let changed = false
        for (const f of others) {
          const g = f.getGeometry() as Polygon | MultiPolygon
          const otherGeo = geoJsonFormat.current.writeGeometryObject(g)
          let otherJsts = jstsReader.read(otherGeo)
          try { otherJsts = otherJsts.buffer(0) } catch {}
          if (reducer) { try { otherJsts = reducer.reduce(otherJsts) } catch {} }

          if (featureJsts.intersects(otherJsts) || featureJsts.touches(otherJsts)) {
            try {
              const diff = featureJsts.difference(otherJsts)
              featureJsts = diff
              try { featureJsts = featureJsts.buffer(0) } catch {}
              if (reducer) { try { featureJsts = reducer.reduce(featureJsts) } catch {} }
              changed = true
            } catch {
              // ignore individual failures
            }
          }
        }

        if (!changed) return false
        if (featureJsts.isEmpty && featureJsts.isEmpty()) return false

        const trimmedGeo = jstsWriter.write(featureJsts)
        const trimmedOl = geoJsonFormat.current.readGeometry(trimmedGeo, {
          dataProjection: "EPSG:3857",
          featureProjection: "EPSG:3857"
        })
        if (trimmedOl) {
          feature.setGeometry(trimmedOl)
          return true
        }
        return false
      } catch {
        return false
      }
    }

    const carveFeatureOutOfOtherClasses = (feature: Feature): boolean => {
      const classId = feature.get("classId")
      if (classId == null) return false
      const geometry = feature.getGeometry()
      if (!(geometry instanceof Polygon) && !(geometry instanceof MultiPolygon)) return false

      try {
        const jsts = (window as any).jsts
        if (!jsts?.io?.GeoJSONReader || !jsts?.io?.GeoJSONWriter) return false

        const reader = new jsts.io.GeoJSONReader()
        const writer = new jsts.io.GeoJSONWriter()
        const featureGeo = geoJsonFormat.current.writeGeometryObject(geometry)
        let carveJsts = reader.read(featureGeo)
        try { carveJsts = carveJsts.buffer(0) } catch {}

        let changed = false
        vectorSource.getFeatures().forEach((other) => {
          if (other === feature) return
          if (other.get(TRANSIENT_KEY) || other.get(POINT_KEY)) return
          const otherClassId = other.get("classId")
          if (otherClassId == null || otherClassId === classId) return
          const otherGeom = other.getGeometry()
          if (!(otherGeom instanceof Polygon || otherGeom instanceof MultiPolygon)) return

          try {
            const otherGeo = geoJsonFormat.current.writeGeometryObject(otherGeom)
            let otherJsts = reader.read(otherGeo)
            try { otherJsts = otherJsts.buffer(0) } catch {}
            if (!(otherJsts.intersects(carveJsts) || otherJsts.touches(carveJsts))) return

            let diff = otherJsts.difference(carveJsts)
            try { diff = diff.buffer(0) } catch {}
            if (!diff || (diff.isEmpty && diff.isEmpty())) {
              vectorSource.removeFeature(other)
              changed = true
              return
            }

            const diffGeo = writer.write(diff)
            const diffOl = geoJsonFormat.current.readGeometry(diffGeo, {
              dataProjection: "EPSG:3857",
              featureProjection: "EPSG:3857"
            })
            if (!diffOl) {
              vectorSource.removeFeature(other)
              changed = true
              return
            }
            other.setGeometry(diffOl)
            changed = true
          } catch {
            // Keep this other feature unchanged if topology fails.
          }
        })

        return changed
      } catch {
        return false
      }
    }

    const selectionStyle = (feature: FeatureLike) => {
      const f = feature as Feature
      const geometry = f.getGeometry()

      if (f.get(POINT_KEY)) {
        return [
          new Style({
            image: new CircleStyle({
              radius: 14,
              fill: new Fill({ color: hexToRgba(POINT_COLOR, 0.25) }),
              stroke: new Stroke({ color: POINT_COLOR, width: 2 })
            })
          }),
          new Style({
            image: new RegularShape({
              points: 5,
              radius: 11,
              radius2: 4.5,
              angle: 0,
              fill: new Fill({ color: POINT_COLOR }),
              stroke: new Stroke({ color: "#ffffff", width: 2 })
            })
          })
        ]
      }

      const isPolygon = geometry instanceof Polygon || geometry instanceof MultiPolygon
      const classId = f.get("classId")
      const paintClass = classesRef.current.find(c => c.id === classId)
      const classColor = paintClass?.color || f.get("strokeColor") || "#2563eb"
      const styles: Style[] = [
        new Style({
          stroke: new Stroke({
            color: classColor,
            width: 3,
            lineDash: [6, 4]
          }),
          fill: isPolygon ? new Fill({ color: hexToRgba(classColor, 0.25) }) : undefined
        }),
        new Style({
          image: new CircleStyle({
            radius: 6,
            fill: new Fill({ color: "#ffffff" }),
            stroke: new Stroke({ color: classColor, width: 2 })
          }),
          geometry: (feat: FeatureLike) => {
            const g = (feat as Feature).getGeometry()
            if (g instanceof Polygon) {
              const rings = g.getCoordinates()
              const points: number[][] = []
              rings.forEach(ring => {
                const endIdx = ring.length > 1 && ring[0][0] === ring[ring.length - 1][0] && ring[0][1] === ring[ring.length - 1][1]
                  ? ring.length - 1
                  : ring.length
                for (let i = 0; i < endIdx; i++) points.push(ring[i])
              })
              return new MultiPoint(points)
            }
            if (g instanceof MultiPolygon) {
              const polys = g.getCoordinates()
              const points: number[][] = []
              polys.forEach(rings => {
                rings.forEach(ring => {
                  const endIdx = ring.length > 1 && ring[0][0] === ring[ring.length - 1][0] && ring[0][1] === ring[ring.length - 1][1]
                    ? ring.length - 1
                    : ring.length
                  for (let i = 0; i < endIdx; i++) points.push(ring[i])
                })
              })
              return new MultiPoint(points)
            }
            if (g instanceof LineString) {
              return new MultiPoint(g.getCoordinates())
            }
            return undefined as any
          }
        })
      ]
      return styles
    }

    const select = new Select({
      condition: clickCondition,
      layers: [vectorLayer],
      filter: (feature) => !feature.get(TRANSIENT_KEY),
      style: selectionStyle as any,
      hitTolerance: 5
    })
    map.addInteraction(select)
    selectInteractionRef.current = select

    const syncSelection = () => {
      const features = select.getFeatures().getArray()
      const next = (features[0] as Feature) ?? null
      if (next && next.get(POINT_KEY)) {
        setSelectedPointFeature(next)
        setSelectedEditFeature(null)
      } else {
        setSelectedEditFeature(next)
        setSelectedPointFeature(null)
      }
      if (!next) setSplitMode(false)
    }
    const selectAddKey = select.getFeatures().on("add" as any, syncSelection)
    const selectRemoveKey = select.getFeatures().on("remove" as any, syncSelection)

    const translate = new Translate({
      features: select.getFeatures(),
      hitTolerance: 0
    })
    map.addInteraction(translate)
    translateInteractionRef.current = translate

    let translateStartGeom: Geometry | null = null
    let translateStartCoord: number[] | null = null
    let translateActiveFeature: Feature | null = null
    let translateSnapIndicator: Feature | null = null

    const clearTranslateSnapIndicator = () => {
      if (translateSnapIndicator && vectorSource.hasFeature(translateSnapIndicator)) {
        vectorSource.removeFeature(translateSnapIndicator)
      }
      translateSnapIndicator = null
    }

    const showTranslateSnapIndicator = (coord: number[]) => {
      if (!translateSnapIndicator) {
        const feat = new Feature({ geometry: new Point(coord) })
        feat.set(TRANSIENT_KEY, true)
        feat.setStyle(new Style({
          image: new CircleStyle({
            radius: 5,
            fill: new Fill({ color: "#22c55e" }),
            stroke: new Stroke({ color: "#ffffff", width: 2 })
          })
        }))
        translateSnapIndicator = feat
        vectorSource.addFeature(feat)
      } else {
        const geom = translateSnapIndicator.getGeometry()
        if (geom instanceof Point) {
          geom.setCoordinates(coord)
        } else {
          translateSnapIndicator.setGeometry(new Point(coord))
        }
      }
    }

    const computeTranslateSnapOffset = (
      geometry: Polygon | MultiPolygon,
      classId: unknown
    ): { dx: number; dy: number; snapPoint: number[] | null } => {
      const resolution = map.getView().getResolution() || 1
      const snapTolerance = resolution * 12

      const otherFeatures = vectorSource.getFeatures().filter(f => {
        if (f.get(TRANSIENT_KEY) || f.get(POINT_KEY)) return false
        if (f === translateActiveFeature) return false
        const otherClassId = f.get("classId")
        if (otherClassId == null || otherClassId === classId) return false
        const g = f.getGeometry()
        return g instanceof Polygon || g instanceof MultiPolygon
      })
      if (otherFeatures.length === 0) return { dx: 0, dy: 0, snapPoint: null }

      const vertices: number[][] = []
      if (geometry instanceof Polygon) {
        geometry.getCoordinates().forEach(ring => {
          const endIdx = ring.length > 1 && ring[0][0] === ring[ring.length - 1][0] && ring[0][1] === ring[ring.length - 1][1]
            ? ring.length - 1
            : ring.length
          for (let i = 0; i < endIdx; i++) vertices.push(ring[i])
        })
      } else {
        geometry.getCoordinates().forEach(polys => {
          polys.forEach(ring => {
            const endIdx = ring.length > 1 && ring[0][0] === ring[ring.length - 1][0] && ring[0][1] === ring[ring.length - 1][1]
              ? ring.length - 1
              : ring.length
            for (let i = 0; i < endIdx; i++) vertices.push(ring[i])
          })
        })
      }

      let bestDx = 0
      let bestDy = 0
      let bestDist = Infinity
      let bestSnapPoint: number[] | null = null

      for (const v of vertices) {
        for (const other of otherFeatures) {
          const g = other.getGeometry() as Polygon | MultiPolygon
          const rings: number[][][] = []
          if (g instanceof Polygon) {
            g.getCoordinates().forEach(r => rings.push(r))
          } else {
            g.getCoordinates().forEach(polys => polys.forEach(r => rings.push(r)))
          }
          for (const ring of rings) {
            if (ring.length < 2) continue
            const line = new LineString(ring)
            const closest = line.getClosestPoint(v)
            const dx = closest[0] - v[0]
            const dy = closest[1] - v[1]
            const dist = Math.hypot(dx, dy)
            if (dist < snapTolerance && dist < bestDist) {
              bestDist = dist
              bestDx = dx
              bestDy = dy
              bestSnapPoint = closest
            }
          }
        }
      }

      return { dx: bestDx, dy: bestDy, snapPoint: bestSnapPoint }
    }

    const translateStartKey = translate.on("translatestart" as any, (event: any) => {
      const feats = event?.features?.getArray?.() ?? []
      const feat = feats[0] as Feature | undefined
      if (!feat) return
      const geom = feat.getGeometry()
      if (!(geom instanceof Polygon) && !(geom instanceof MultiPolygon)) {
        translateStartGeom = null
        translateActiveFeature = null
        translateStartCoord = null
        return
      }
      translateActiveFeature = feat
      translateStartGeom = geom.clone()
      translateStartCoord = event.coordinate
    })

    const translatingKey = translate.on("translating" as any, (event: any) => {
      if (!snapToBoundaryEnabled) return
      if (!translateStartGeom || !translateStartCoord || !translateActiveFeature) return
      const currentCoord = event.coordinate
      const dx = currentCoord[0] - translateStartCoord[0]
      const dy = currentCoord[1] - translateStartCoord[1]

      const translated = translateStartGeom.clone() as Polygon | MultiPolygon
      translated.translate(dx, dy)

      const { dx: snapDx, dy: snapDy, snapPoint } = computeTranslateSnapOffset(
        translated,
        translateActiveFeature.get("classId")
      )
      if (snapDx !== 0 || snapDy !== 0) {
        translated.translate(snapDx, snapDy)
      }

      translateActiveFeature.setGeometry(translated)

      if (snapPoint) {
        showTranslateSnapIndicator(snapPoint)
      } else {
        clearTranslateSnapIndicator()
      }
    })

    const modify = new Modify({
      features: select.getFeatures(),
      pixelTolerance: 12
    })
    map.addInteraction(modify)
    modifyInteractionRef.current = modify

    let snap: Snap | null = null
    if (snapToBoundaryEnabled) {
      snap = new Snap({
        source: vectorSource,
        pixelTolerance: 10
      })
      map.addInteraction(snap)
    }

    const handlePointerMove = (event: MapBrowserEvent<UIEvent>) => {
      if (event.dragging) return
      const viewport = map.getViewport()
      if (!viewport) return
      const selectedFeatures = select.getFeatures().getArray()
      let hitSelected = false
      let hitAny = false
      map.forEachFeatureAtPixel(
        event.pixel,
        (feature, layer) => {
          if (layer !== vectorLayer) return false
          if ((feature as Feature).get(TRANSIENT_KEY)) return false
          hitAny = true
          if (selectedFeatures.includes(feature as Feature)) {
            hitSelected = true
            return true
          }
          return false
        },
        { hitTolerance: 5 }
      )
      if (hitSelected) {
        viewport.style.cursor = "move"
      } else if (hitAny) {
        viewport.style.cursor = "pointer"
      } else {
        viewport.style.cursor = "grab"
      }
    }

    const pointerMoveKey = map.on("pointermove" as any, handlePointerMove)

    const modifyEndKey = modify.on("modifyend" as any, (event: any) => {
      const features = event?.features?.getArray?.() ?? []
      if (updateVerticesOnOverlapEnabled) {
        features.forEach((f: Feature) => carveFeatureOutOfOtherClasses(f))
      } else if (snapToBoundaryEnabled) {
        features.forEach((f: Feature) => trimFeatureAgainstOtherClasses(f))
      }
      saveState()
    })
    const translateEndKey = translate.on("translateend" as any, (event: any) => {
      const features = event?.features?.getArray?.() ?? []
      if (updateVerticesOnOverlapEnabled) {
        features.forEach((f: Feature) => carveFeatureOutOfOtherClasses(f))
      } else if (snapToBoundaryEnabled) {
        features.forEach((f: Feature) => trimFeatureAgainstOtherClasses(f))
      }
      clearTranslateSnapIndicator()
      translateStartGeom = null
      translateStartCoord = null
      translateActiveFeature = null
      saveState()
    })

    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement
      if (target?.tagName === "INPUT" || target?.tagName === "TEXTAREA" || target?.isContentEditable) return
      if (event.key === "Escape") {
        select.getFeatures().clear()
      } else if (event.key === "Delete" || event.key === "Backspace") {
        const selected = select.getFeatures().getArray().slice()
        if (selected.length > 0) {
          event.preventDefault()
          selected.forEach(f => {
            if (vectorSourceRef.current?.hasFeature(f as Feature)) {
              vectorSourceRef.current.removeFeature(f as Feature)
            }
          })
          select.getFeatures().clear()
          saveState()
        }
      }
    }
    window.addEventListener("keydown", handleKeyDown)

    return () => {
      map.removeInteraction(select)
      map.removeInteraction(modify)
      map.removeInteraction(translate)
      if (snap) map.removeInteraction(snap)
      unByKey(pointerMoveKey)
      unByKey(modifyEndKey)
      unByKey(translateEndKey)
      unByKey(translateStartKey)
      unByKey(translatingKey)
      unByKey(selectAddKey)
      unByKey(selectRemoveKey)
      clearTranslateSnapIndicator()
      window.removeEventListener("keydown", handleKeyDown)
      selectInteractionRef.current = null
      modifyInteractionRef.current = null
      translateInteractionRef.current = null
      setSelectedEditFeature(null)
      setSelectedPointFeature(null)
      setSplitMode(false)
      const viewport = map.getViewport()
      if (viewport) viewport.style.cursor = ""
    }
  }, [map, activeTool, saveState, snapToBoundaryEnabled, updateVerticesOnOverlapEnabled])

  // Change selected feature's class (Cursor mode panel)
  const handleChangeSelectedFeatureClass = React.useCallback((newClassId: number) => {
    const feature = selectedEditFeature
    if (!feature) return
    const newClass = classesRef.current.find(c => c.id === newClassId)
    if (!newClass) return
    const classOpacity = newClass.opacity ?? 1
    const effectiveOpacity = classOpacity * opacity
    feature.set("classId", newClass.id)
    feature.set("strokeColor", newClass.color)
    feature.set("opacity", effectiveOpacity)
    styleCacheRef.current = {}
    vectorLayerRef.current?.changed()
    selectInteractionRef.current?.getFeatures().changed()
    feature.changed()
    setSelectedEditFeature(feature)
    saveState()
  }, [selectedEditFeature, opacity, saveState])

  // Split mode — draw a line across the selected polygon
  React.useEffect(() => {
    if (!map || !vectorSourceRef.current) return
    if (activeTool !== ToolMode.Cursor) return
    if (!splitMode || !selectedEditFeature) return

    const source = vectorSourceRef.current
    const feature = selectedEditFeature
    const geometry = feature.getGeometry()
    if (!(geometry instanceof Polygon) && !(geometry instanceof MultiPolygon)) {
      setSplitMode(false)
      return
    }

    const modify = modifyInteractionRef.current
    const translate = translateInteractionRef.current
    const select = selectInteractionRef.current
    modify?.setActive(false)
    translate?.setActive(false)
    select?.setActive(false)

    const viewport = map.getViewport()
    if (viewport) viewport.style.cursor = "crosshair"

    const drawSource = new VectorSource()
    const drawLayer = new VectorLayer({
      source: drawSource,
      style: new Style({
        stroke: new Stroke({
          color: "#ef4444",
          width: 2,
          lineDash: [6, 4]
        }),
        image: new CircleStyle({
          radius: 4,
          fill: new Fill({ color: "#ef4444" }),
          stroke: new Stroke({ color: "#ffffff", width: 2 })
        })
      }),
      zIndex: 1000
    })
    map.addLayer(drawLayer)

    const draw = new Draw({
      source: drawSource,
      type: "LineString",
      style: new Style({
        stroke: new Stroke({
          color: "#ef4444",
          width: 2,
          lineDash: [6, 4]
        }),
        image: new CircleStyle({
          radius: 4,
          fill: new Fill({ color: "#ef4444" }),
          stroke: new Stroke({ color: "#ffffff", width: 2 })
        })
      })
    })
    map.addInteraction(draw)

    const splitFeatureWithLine = (
      originalFeature: Feature,
      splitLine: LineString
    ): Feature[] => {
      const originalGeom = originalFeature.getGeometry()
      if (!(originalGeom instanceof Polygon) && !(originalGeom instanceof MultiPolygon)) return []

      try {
        const jsts = (window as any).jsts
        if (!jsts?.io?.GeoJSONReader || !jsts?.io?.GeoJSONWriter || !jsts?.operation?.polygonize?.Polygonizer) {
          console.warn("JSTS polygonizer not available; cannot split.")
          return []
        }

        const reader = new jsts.io.GeoJSONReader()
        const writer = new jsts.io.GeoJSONWriter()

        const polyGeo = geoJsonFormat.current.writeGeometryObject(originalGeom)
        const lineGeo = geoJsonFormat.current.writeGeometryObject(splitLine)

        let polyJsts = reader.read(polyGeo)
        try { polyJsts = polyJsts.buffer(0) } catch {}
        const lineJsts = reader.read(lineGeo)

        if (!polyJsts.intersects(lineJsts)) return []

        const boundary = polyJsts.getBoundary()
        const union = boundary.union(lineJsts)

        const polygonizer = new jsts.operation.polygonize.Polygonizer()
        polygonizer.add(union)
        const polygons = polygonizer.getPolygons()

        const kept: any[] = []
        const iter = polygons.iterator()
        while (iter.hasNext()) {
          const p = iter.next()
          try {
            const interior = p.getInteriorPoint()
            if (polyJsts.contains(interior)) kept.push(p)
          } catch {
            try {
              const centroid = p.getCentroid()
              if (polyJsts.contains(centroid)) kept.push(p)
            } catch {}
          }
        }

        if (kept.length < 2) return []

        return kept.map(g => {
          const geoJson = writer.write(g)
          const newGeom = geoJsonFormat.current.readGeometry(geoJson, {
            dataProjection: "EPSG:3857",
            featureProjection: "EPSG:3857"
          })
          const newFeature = new Feature({ geometry: newGeom })
          newFeature.set("classId", originalFeature.get("classId"))
          newFeature.set("strokeColor", originalFeature.get("strokeColor"))
          newFeature.set("opacity", originalFeature.get("opacity"))
          return newFeature
        })
      } catch (e) {
        console.warn("Split failed:", e)
        return []
      }
    }

    const drawEndKey = draw.on("drawend" as any, (event: any) => {
      const drawnFeature = event.feature as Feature
      const line = drawnFeature.getGeometry() as LineString
      if (!line) {
        setSplitMode(false)
        return
      }

      const pieces = splitFeatureWithLine(feature, line)
      if (pieces.length >= 2) {
        if (source.hasFeature(feature)) source.removeFeature(feature)
        pieces.forEach(p => source.addFeature(p))
        select?.getFeatures().clear()
        saveState()
      } else {
        console.warn("Split line did not divide the polygon; draw a line that crosses the shape.")
      }

      setSplitMode(false)
    })

    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement
      if (target?.tagName === "INPUT" || target?.tagName === "TEXTAREA" || target?.isContentEditable) return
      if (event.key === "Escape") {
        event.preventDefault()
        setSplitMode(false)
      }
    }
    window.addEventListener("keydown", handleKeyDown)

    return () => {
      unByKey(drawEndKey)
      map.removeInteraction(draw)
      map.removeLayer(drawLayer)
      drawSource.clear()
      window.removeEventListener("keydown", handleKeyDown)
      modify?.setActive(true)
      translate?.setActive(true)
      select?.setActive(true)
      if (viewport) viewport.style.cursor = ""
    }
  }, [map, activeTool, splitMode, selectedEditFeature, saveState])

  return (
    <div style={{ width: "100%", height: "100%", minHeight: 0, minWidth: 0, position: "relative" }}>
      {localGeotiffLoading && (
        <div
          className="bg-dark text-light rounded-sm px-3 py-2 position-absolute"
          style={{ top: "0.75em", left: "50%", transform: "translateX(-50%)", zIndex: 20 }}
        >
          <i className="fas fa-spinner fa-spin mr-2" aria-hidden />
          Loading GeoTIFF…
        </div>
      )}
      {localGeotiffDropMessage && (
        <div
          className="bg-warning text-dark rounded-sm px-3 py-2 position-absolute"
          style={{
            top: localGeotiffLoading ? "3.5em" : "0.75em",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 20,
            maxWidth: "min(90%, 28rem)",
          }}
          role="alert"
        >
          {localGeotiffDropMessage}
          <button
            type="button"
            className="close float-right ml-2"
            aria-label="Dismiss"
            onClick={() => setLocalGeotiffDropMessage(null)}
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
      )}
      <div style={{ width: "100%", height: "100%", minHeight: 0 }} ref={mapRef} />

      {/* Point note popup — anchored to the point via an OL Overlay */}
      <div ref={pointPopupElementRef} className="ol-point-popup">
        {selectedPointFeature && (activeTool === ToolMode.Cursor || activeTool === ToolMode.Point) && (
          <div style={{
            position: "relative",
            background: "#ffffff",
            borderRadius: "8px",
            boxShadow: "0 4px 16px rgba(0,0,0,0.18)",
            border: `1px solid ${POINT_COLOR}`,
            padding: "12px 14px",
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            width: "min(320px, 80vw)"
          }}>
            <div style={{
              position: "absolute",
              left: "50%",
              bottom: "-7px",
              transform: "translateX(-50%) rotate(45deg)",
              width: "12px",
              height: "12px",
              background: "#ffffff",
              borderRight: `1px solid ${POINT_COLOR}`,
              borderBottom: `1px solid ${POINT_COLOR}`
            }} />
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill={POINT_COLOR} stroke={POINT_COLOR} strokeWidth="2" strokeLinejoin="round">
                <polygon points="12,2 15,9 22,9.5 17,14 18.5,21 12,17.5 5.5,21 7,14 2,9.5 9,9" />
              </svg>
              <span style={{ fontWeight: 600, fontSize: "13px", color: "#1f2d3d", flex: 1 }}>
                Point note
              </span>
              <button
                onClick={() => {
                  const feature = selectedPointFeature
                  if (!feature || !vectorSourceRef.current) return
                  if (vectorSourceRef.current.hasFeature(feature)) {
                    vectorSourceRef.current.removeFeature(feature)
                  }
                  selectInteractionRef.current?.getFeatures().clear()
                  setSelectedPointFeature(null)
                  saveState()
                }}
                title="Delete point"
                className="btn btn-sm btn-outline-danger"
                style={{ padding: "2px 8px", fontSize: "12px" }}
              >
                <i className="fas fa-trash" /> Delete
              </button>
              <button
                onClick={() => {
                  selectInteractionRef.current?.getFeatures().clear()
                  setSelectedPointFeature(null)
                }}
                title="Close"
                className="btn btn-sm"
                style={{ padding: "2px 6px", background: "transparent", color: "#8492a6" }}
              >
                <i className="fas fa-times" />
              </button>
            </div>
            <textarea
              value={pointNoteDraft}
              onChange={(e) => {
                const v = e.target.value
                setPointNoteDraft(v)
                selectedPointFeature.set("note", v)
              }}
              onBlur={() => saveState()}
              placeholder="Add a note..."
              rows={3}
              autoFocus
              className="form-control form-control-sm"
              style={{ resize: "vertical", minHeight: "60px", fontSize: "13px" }}
            />
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button
                onClick={() => {
                  selectedPointFeature.set("note", pointNoteDraft)
                  saveState()
                  selectInteractionRef.current?.getFeatures().clear()
                  setSelectedPointFeature(null)
                }}
                className="btn btn-sm"
                style={{
                  background: POINT_COLOR,
                  color: "#ffffff",
                  border: `1px solid ${POINT_COLOR}`,
                  padding: "4px 14px",
                  fontSize: "12px",
                  fontWeight: 600
                }}
              >
                <i className="fas fa-check mr-1" /> Confirm
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Selected shape panel (Cursor mode) */}
      {activeTool === ToolMode.Cursor && selectedEditFeature && (() => {
        const currentClassId = selectedEditFeature.get("classId")
        const currentClass = classes.find(c => c.id === currentClassId)
        const accentColor = currentClass?.color || "#4a6cf7"
        const geometry = selectedEditFeature.getGeometry()
        const isPolygonal = geometry instanceof Polygon || geometry instanceof MultiPolygon
        return (
          <div style={{
            position: "absolute",
            top: "16px",
            left: "50%",
            transform: "translateX(-50%)",
            background: "#ffffff",
            borderRadius: "8px",
            boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
            border: `1px solid ${accentColor}`,
            padding: "10px 14px",
            display: "flex",
            alignItems: "center",
            gap: "12px",
            zIndex: 200,
            fontSize: "13px"
          }}>
            <span style={{ color: "#8492a6", fontWeight: 600, fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.05em" }}>
              Selected
            </span>
            <div style={{
              width: "14px",
              height: "14px",
              borderRadius: "4px",
              background: accentColor,
              flexShrink: 0,
              boxShadow: `0 0 0 2px ${accentColor}33`
            }} />
            <label style={{ display: "flex", alignItems: "center", gap: "6px", color: "#1f2d3d", marginBottom: 0 }}>
              <span style={{ fontWeight: 500 }}>Class:</span>
              <select
                value={currentClassId != null ? String(currentClassId) : ""}
                onChange={(e) => {
                  const v = e.target.value
                  if (v === "") return
                  handleChangeSelectedFeatureClass(Number(v))
                }}
                className="form-control form-control-sm"
                style={{ minWidth: "140px", padding: "2px 8px", fontSize: "13px" }}
              >
                {currentClass == null && <option value="">(no class)</option>}
                {classes.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </label>
            <button
              onClick={() => setSplitMode(true)}
              disabled={splitMode || !isPolygonal}
              title="Split shape with a line"
              className="btn btn-sm btn-outline-secondary"
              style={{ padding: "2px 10px", fontSize: "12px", opacity: isPolygonal ? 1 : 0.5 }}
            >
              <i className="fas fa-cut mr-1" />
              {splitMode ? "Drawing split..." : "Split"}
            </button>
            <button
              onClick={() => {
                const feature = selectedEditFeature
                if (!feature || !vectorSourceRef.current) return
                if (vectorSourceRef.current.hasFeature(feature)) {
                  vectorSourceRef.current.removeFeature(feature)
                }
                selectInteractionRef.current?.getFeatures().clear()
                saveState()
              }}
              title="Delete shape (Delete)"
              className="btn btn-sm btn-outline-danger"
              style={{ padding: "2px 10px", fontSize: "12px" }}
            >
              <i className="fas fa-trash mr-1" />
              Delete
            </button>
            <button
              onClick={() => selectInteractionRef.current?.getFeatures().clear()}
              title="Deselect (Esc)"
              className="btn btn-sm"
              style={{ padding: "2px 6px", background: "transparent", color: "#8492a6" }}
            >
              <i className="fas fa-times" />
            </button>
          </div>
        )
      })()}

      {/* Split-mode hint banner */}
      {activeTool === ToolMode.Cursor && splitMode && selectedEditFeature && (
        <div style={{
          position: "absolute",
          top: "70px",
          left: "50%",
          transform: "translateX(-50%)",
          background: "#fff1f2",
          color: "#b91c1c",
          border: "1px solid #fca5a5",
          padding: "8px 14px",
          borderRadius: "6px",
          fontSize: "12px",
          zIndex: 200,
          display: "flex",
          alignItems: "center",
          gap: "10px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.08)"
        }}>
          <i className="fas fa-cut" />
          <span>Draw a line across the shape to split it. Double-click to finish. Esc to cancel.</span>
        </div>
      )}
    </div>
  )
})

PaintbrushMap.displayName = "PaintbrushMap"
